import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { STATUTS_DOSSIER } from './OutilSinistre';
import DocsDossier from './DocsDossier';
import DocumentsDossier from './DocumentsDossier';
import { COUNTRIES } from './countries';
import { theme, pageTitleStyle, statutBadgeStyle, sectionCardStyle, sectionHeaderRow, sectionTitleStyle, sectionIconWrap, sectionIntroStyle, sectionFooterStyle, lockButtonStyle, sectionIconPaths, secondaryButtonStyle } from '../../theme';

const BASE = '/api';
function authFetch(url, opts={}) {
  const token = localStorage.getItem('arcus_token');
  return fetch(url, { ...opts, headers: { ...(opts.headers||{}), ...(token?{Authorization:`Bearer ${token}`}:{}) } });
}

const lbl = { fontSize:12, fontWeight:500, color:'#64748B', display:'block', marginBottom:6 };
const inp = { border:'1px solid #D9D9D5', borderRadius:8, padding:'10px 12px', fontSize:14,
  boxSizing:'border-box', width:'100%', fontFamily:'inherit', outline:'none' };

const ORANGE = '#F18A00';
const BLACK  = '#161616';

// Astérisque rouge pour signaler un champ obligatoire.
const Req = () => <span style={{ color:'#DC2626', marginLeft:3, fontWeight:700 }}>*</span>;
const SvgIcon = ({ size = 16, color = 'currentColor', children }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    {children}
  </svg>
);

// Icône de section : pastille orange discrète + glyphe Tabler professionnel.
const SectionIcon = ({ name }) => (
  <span style={sectionIconWrap}>
    <svg width={17} height={17} viewBox="0 0 24 24" fill="none"
      stroke={theme.orange} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"
      dangerouslySetInnerHTML={{ __html: sectionIconPaths[name] || '' }} />
  </span>
);

// Titre de section uniforme (icône + libellé + badge "verrouillé" optionnel).
const SectionTitle = ({ icon, children, locked, lockedLabel }) => (
  <div style={sectionTitleStyle}>
    <SectionIcon name={icon} />
    {children}
    {locked && (
      <span style={{ fontSize:11, color:'#92400E', background:'#FFFBEB', padding:'2px 9px', borderRadius:99, fontWeight:500,
        display:'inline-flex', alignItems:'center', gap:4 }}>
        <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="#92400E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          dangerouslySetInnerHTML={{ __html: sectionIconPaths.lock }} />
        {lockedLabel}
      </span>
    )}
  </div>
);

const TYPES = { particulier:'Particulier', professionnel:'Professionnel' };

function VoyageLecture({ dossier, souhait, t, onAppelFonds }) {
  const s = souhait || dossier.souhait_voyageur;
  const acompteColor = s === 'remboursement'   ? '#059669' : '#1A1A1A';
  const soldeColor   = s === 'maintien_voyage' ? '#059669' : '#1A1A1A';
  const fmt = v => v != null ? v.toLocaleString('fr-FR', { minimumFractionDigits:2 }) + ' €' : '—';
  const solde = dossier.montant_total != null ? dossier.montant_total - (dossier.acompte_paye||0) : null;

  return (
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, rowGap:16 }}>
      <div>
        <div style={{ fontSize:11, color:'#64748B', fontWeight:600, marginBottom:2 }}>
          {t('sinistres.voyage_montant_total').replace(' (€)','').toUpperCase()}
        </div>
        <div style={{ fontSize:14, fontWeight:600, color:'#1A1A1A' }}>
          {fmt(dossier.montant_total)}
        </div>
      </div>
      <div>
        <div style={{ fontSize:11, color:'#64748B', fontWeight:600, marginBottom:2 }}>
          {t('sinistres.voyage_acompte').replace(' (€)','').toUpperCase()}
        </div>
        <div style={{ fontSize:14, fontWeight:700, color:acompteColor }}>
          {fmt(dossier.acompte_paye)}
        </div>
      </div>
      <div style={{ gridColumn:'1/-1' }}>
        <div style={{ fontSize:11, color:'#64748B', fontWeight:600, marginBottom:2 }}>
          {t('sinistres.voyage_solde').replace(' (€)','').toUpperCase()}
        </div>
        <div style={{ fontSize:16, fontWeight:800, color: dossier.montant_total != null ? soldeColor : '#94A3B8' }}>
          {fmt(solde)}
        </div>
      </div>
    </div>
  );
}

// Phase D4 : ligne de checklist (condition remplie ou non)
function CheckRow({ ok, label }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:13 }}>
      <span style={{ width:18, height:18, borderRadius:'50%', flexShrink:0,
        display:'inline-flex', alignItems:'center', justifyContent:'center',
        background: ok ? '#D1FAE5' : '#FEE2E2', color: ok ? '#059669' : '#DC2626',
        fontSize:11, fontWeight:800 }}>
        {ok ? '✓' : '×'}
      </span>
      <span style={{ color: ok ? '#334155' : '#94A3B8' }}>{label}</span>
    </div>
  );
}

export default function FicheDossier() {
  const { procedureId, dossierId } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { isManager, isVoyageur, isArcus, isIT, user, isReadOnly: isAccountsReadOnly } = useAuth();
  const isAccounts = user?.role === 'accounts';
  const isSouscripteur = user?.role === 'souscripteur';
  // Permissions workflow Phase B
  const canSendValidation = isSouscripteur || isIT;
  const canValidate       = isManager || isIT;
  const canMarkPaid       = isAccounts || isIT;
  // Phase D4 : qui peut verrouiller/déverrouiller les sections
  const canLock           = isSouscripteur || isIT || isManager;

  const [dossier, setDossier]   = useState(null);
  // Verrouillage total pendant l'attente de validation manager (l'admin ne peut plus rien modifier).
  // Le Manager/IT garde la main pour valider/refuser.
  const validationLock = dossier?.statut === 'attente_validation' && !canValidate;
  // Read-only effectif = lecture seule Accounts OU verrouillage d'attente de validation.
  const isReadOnly = isAccountsReadOnly || validationLock;
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [changingStatut, setChangingStatut] = useState(false);
  const [editVoyageur, setEditVoyageur]     = useState(false);
  const [savingVoyageur, setSavingVoyageur] = useState(false);
  const [editFinance, setEditFinance]       = useState(false);
  const [savingFinance, setSavingFinance]   = useState(false);
  const [montantTotal, setMontantTotal]     = useState('');
  const [acomptePaye, setAcomptePaye]       = useState('');
  const [showAppelFonds, setShowAppelFonds] = useState(false);
  const [appelCopied, setAppelCopied]       = useState(false);
  const [dateLimite, setDateLimite]           = useState('');
  const [pdfLoading, setPdfLoading]           = useState(false);
  // ── Phase D4 : verrous de sections + sanctions screening ──
  const [benefLocked, setBenefLocked]         = useState(false);
  const [voyageLocked, setVoyageLocked]       = useState(false);
  const [paiementsLocked, setPaiementsLocked] = useState(false);
  const [sanctionDone, setSanctionDone]       = useState(false);
  const [dateNaissance, setDateNaissance]     = useState('');
  const [appelFondsDoc, setAppelFondsDoc]     = useState(null); // doc appel de fonds (pour savoir s'il est payé)
  const [lockBusy, setLockBusy]               = useState(false);
  // Phase D4-B : état pièces + factures pour la validation conditionnelle
  const [docsAllApproved, setDocsAllApproved] = useState(false);
  const [docsCount, setDocsCount]             = useState(0);
  const [piecesList, setPiecesList]           = useState([]);
  const [facturesParPaiement, setFacturesParPaiement] = useState([]);
  // ── Validation administrative (sur la forme) + paiements reçus du solde ──
  const [adminValide, setAdminValide]         = useState(false);
  const [adminValidating, setAdminValidating] = useState(false);
  const [paiementsSolde, setPaiementsSolde]   = useState([]);
  const [savingSolde, setSavingSolde]         = useState(false);
  const [soldeLocked, setSoldeLocked]         = useState(false);
  const [soldeConfirme, setSoldeConfirme]     = useState(false);
  const [showSoldeSaveConfirm, setShowSoldeSaveConfirm] = useState(false);
  const [showSoldeRecuConfirm, setShowSoldeRecuConfirm] = useState(false);
  const [showVoyageurModal, setShowVoyageurModal] = useState(false);
  const [voyageurEmail, setVoyageurEmail]     = useState('');
  const [voyageurPwd, setVoyageurPwd]         = useState('');
  // ── Phase B : modals workflow ─────────────────────────────
  const [showRefuseModal, setShowRefuseModal] = useState(false);
  const [refuseMotif, setRefuseMotif]         = useState('');
  const [showMarkPaidConfirm, setShowMarkPaidConfirm] = useState(false);
  const [showSendValidationConfirm, setShowSendValidationConfirm] = useState(false);
  const [showStartProcessing, setShowStartProcessing] = useState(false);
  const [startingProcessing, setStartingProcessing] = useState(false);
  // ── Phase D3 : soumission voyageur ────────────────────────
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  // ── Phase D4 : décliner le dossier ────────────────────────
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [declineMotif, setDeclineMotif]         = useState('');
  const [voyageurSaving, setVoyageurSaving]   = useState(false);
  const [voyageurCreated, setVoyageurCreated] = useState(null);
  const [voyageurError, setVoyageurError]     = useState('');
  // Souhait voyageur
  const [souhaitVoyageur, setSouhaitVoyageur] = useState('');
  // Paiement
  const [paiements, setPaiements]             = useState([]);
  const [savingPaiement, setSavingPaiement]   = useState(false);
  // Phase C : prestataires liés au dossier (utilisés quand souhait = maintien)
  const [prestairesDossier, setPrestairesDossier] = useState([]);
  const [allPrestataires, setAllPrestataires]     = useState([]);
  const [savingPrestataires, setSavingPrestataires] = useState(false);
  // Voyageur form
  const [titre, setTitre]       = useState('');
  const [prenom, setPrenom]     = useState('');
  const [nomV, setNomV]         = useState('');
  const [email, setEmail]       = useState('');
  const [telephone, setTel]           = useState('');
  // Qualification du sinistre
  const [montantFacture, setMontantFacture]       = useState('');
  const [typeReglement, setTypeReglement]         = useState('');
  const [montantAcompteClient, setMontantAcompteClient] = useState('');
  const [modesPaiementClient, setModesPaiementClient]   = useState([]);
  const [nbVoyageurs, setNbVoyageurs]             = useState('');
  const [dateDepart, setDateDepart]               = useState('');
  const [voyageursList, setVoyageursList]         = useState([]);
  const [souhaitClient, setSouhaitClient]         = useState('');

  useEffect(() => {
    authFetch(`${BASE}/sinistres/${procedureId}/dossiers/${dossierId}`)
      .then(r => r.json())
      .then(d => {
        if (d.error) setError(d.error);
        else {
          setDossier(d);
          setTitre(d.titre || '');
          setPrenom(d.prenom || '');
          setNomV(d.nom_voyageur || '');
          setEmail(d.email_voyageur || '');
          setTel(d.telephone_voyageur || '');
          setMontantFacture(d.montant_facture != null ? String(d.montant_facture) : '');
          setTypeReglement(d.type_reglement || '');
          setMontantAcompteClient(d.montant_acompte_client != null ? String(d.montant_acompte_client) : '');
          setModesPaiementClient(Array.isArray(d.modes_paiement_client) ? d.modes_paiement_client : []);
          setNbVoyageurs(d.nb_voyageurs != null ? String(d.nb_voyageurs) : '');
          setDateDepart(d.date_depart_voyage ? String(d.date_depart_voyage).slice(0,10) : '');
          setVoyageursList(Array.isArray(d.voyageurs) ? d.voyageurs : []);
          setSouhaitClient(d.souhait_client || '');
          setMontantTotal(d.montant_total != null ? String(d.montant_total) : '');
          setSouhaitVoyageur(d.souhait_voyageur || '');
          setDateLimite(d.date_limite_paiement?.slice(0,10) || '');
          if (d.email_voyageur) setVoyageurEmail(d.email_voyageur);
          setPaiements(Array.isArray(d.paiements) ? d.paiements : []);
          setAcomptePaye(d.acompte_paye   != null ? String(d.acompte_paye)  : '');
          // Phase C : prestataires liés (présents quand souhait = maintien)
          setPrestairesDossier(Array.isArray(d.prestataires) ? d.prestataires : []);
          // Phase D4 : verrous + sanctions
          setBenefLocked(!!d.infos_beneficiaire_locked);
          setVoyageLocked(!!d.infos_voyage_locked);
          setPaiementsLocked(!!d.paiements_locked);
          setSanctionDone(!!d.sanction_check_done);
          setDateNaissance(d.date_naissance_beneficiaire?.slice(0,10) || '');
          // Validation administrative + paiements reçus du solde
          setAdminValide(!!d.dossier_admin_valide);
          setPaiementsSolde(Array.isArray(d.paiements_solde) ? d.paiements_solde : []);
          setSoldeLocked(!!d.paiements_solde_locked);
          setSoldeConfirme(!!d.solde_recu_confirme);
        }
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [procedureId, dossierId]);

  // Phase C : charger la liste de tous les prestataires (pour le dropdown)
  useEffect(() => {
    if (isVoyageur) return; // voyageur n'a pas accès
    authFetch(`${BASE}/prestataires`)
      .then(r => r.ok ? r.json() : [])
      .then(setAllPrestataires)
      .catch(() => setAllPrestataires([]));
  }, [isVoyageur]);

  // Phase D4 : charger l'état des pièces (toutes approuvées ?), l'appel de fonds, les factures
  const reloadDocsStatus = useCallback(async () => {
    try {
      // Pièces justificatives
      const pieces = await authFetch(`${BASE}/dossiers/${dossierId}/docs?categorie=piece`).then(r => r.ok ? r.json() : []);
      setDocsCount(pieces.length);
      setPiecesList(Array.isArray(pieces) ? pieces : []);
      setDocsAllApproved(pieces.length > 0 && pieces.every(d => d.statut === 'accepte'));
      // Appel de fonds (catégorie séparée)
      const af = await authFetch(`${BASE}/dossiers/${dossierId}/docs?categorie=appel_fonds`).then(r => r.ok ? r.json() : []);
      setAppelFondsDoc(af && af.length ? af[0] : null);
      // Factures prestataires
      const fac = await authFetch(`${BASE}/dossiers/${dossierId}/docs?categorie=facture`).then(r => r.ok ? r.json() : []);
      setFacturesParPaiement(Array.isArray(fac) ? fac : []);
    } catch { /* silent */ }
  }, [dossierId]);

  useEffect(() => { if (dossierId) reloadDocsStatus(); }, [dossierId, reloadDocsStatus]);

  async function saveVoyageur() {
    setSavingVoyageur(true); setError('');
    try {
      // ── Phase D6-fix : transvasement bénéficiaire → voyage ──
      // Montant total du voyage = montant facturé
      const vMontantTotal = montantFacture !== '' ? parseFloat(montantFacture) : null;
      // Acompte payé : si 'acompte' → montant acompte ; si 'totalite' → montant facturé
      let vAcompte = null;
      if (typeReglement === 'acompte') vAcompte = montantAcompteClient !== '' ? parseFloat(montantAcompteClient) : null;
      else if (typeReglement === 'totalite') vAcompte = vMontantTotal;
      // Souhait voyage dérivé du souhait bénéficiaire (maintien → maintien_voyage)
      const vSouhait = souhaitClient === 'maintien' ? 'maintien_voyage' : (souhaitClient || null);

      const res  = await authFetch(`${BASE}/sinistres/${procedureId}/dossiers/${dossierId}`, {
        method:'PUT', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({
          titre, prenom, nom_voyageur:nomV,
          email_voyageur:email, telephone_voyageur:telephone,
          montant_facture: vMontantTotal,
          type_reglement: typeReglement || null,
          montant_acompte_client: montantAcompteClient !== '' ? parseFloat(montantAcompteClient) : null,
          modes_paiement_client: modesPaiementClient,
          nb_voyageurs: voyageursList.length > 0 ? voyageursList.length : (nbVoyageurs !== '' ? parseInt(nbVoyageurs) : null),
          date_depart_voyage: dateDepart || null,
          voyageurs: voyageursList,
          souhait_client: souhaitClient || null,
          // Champs voyage transvasés :
          montant_total: vMontantTotal,
          acompte_paye: vAcompte,
          souhait_voyageur: vSouhait,
        }),
      });
      let data = {};
      try { data = await res.json(); } catch(_) { data = {}; }
      if (!res.ok || data.error) {
        setError(data.error || `Erreur serveur (${res.status}) lors de l'enregistrement.`);
        return;
      }
      // On relit le dossier depuis le serveur pour confirmer la persistance réelle
      const fresh = await authFetch(`${BASE}/sinistres/${procedureId}/dossiers/${dossierId}`).then(r => r.json());
      if (!fresh.error) {
        setDossier(fresh);
        setTitre(fresh.titre || '');
        setPrenom(fresh.prenom || '');
        setNomV(fresh.nom_voyageur || '');
        setEmail(fresh.email_voyageur || '');
        setTel(fresh.telephone_voyageur || '');
        setMontantFacture(fresh.montant_facture != null ? String(fresh.montant_facture) : '');
        setTypeReglement(fresh.type_reglement || '');
        setMontantAcompteClient(fresh.montant_acompte_client != null ? String(fresh.montant_acompte_client) : '');
        setModesPaiementClient(Array.isArray(fresh.modes_paiement_client) ? fresh.modes_paiement_client : []);
        setNbVoyageurs(fresh.nb_voyageurs != null ? String(fresh.nb_voyageurs) : '');
        setDateDepart(fresh.date_depart_voyage ? String(fresh.date_depart_voyage).slice(0,10) : '');
        setVoyageursList(Array.isArray(fresh.voyageurs) ? fresh.voyageurs : []);
        setSouhaitClient(fresh.souhait_client || '');
        setMontantTotal(fresh.montant_total != null ? String(fresh.montant_total) : '');
        setAcomptePaye(fresh.acompte_paye != null ? String(fresh.acompte_paye) : '');
        setSouhaitVoyageur(fresh.souhait_voyageur || '');
        setSuccessMsg(t('common.saved') || 'Enregistré');
        setTimeout(() => setSuccessMsg(''), 2500);
      } else {
        setDossier(prev => ({ ...prev, ...data }));
      }
    } catch(e) { setError(e.message); }
    finally { setSavingVoyageur(false); }
  }

  async function saveFinance() {
    setSavingFinance(true); setError('');
    try {
      const res  = await authFetch(`${BASE}/sinistres/${procedureId}/dossiers/${dossierId}`, {
        method:'PUT', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({
          montant_total:    montantTotal !== '' ? parseFloat(montantTotal) : null,
          acompte_paye:     acomptePaye  !== '' ? parseFloat(acomptePaye)  : null,
          souhait_voyageur: souhaitVoyageur || null,
        }),
      });
      const data = await res.json();
      if (data.error) { setError(data.error); return; }
      setDossier(prev => ({ ...prev, ...data }));
      setEditFinance(false);
    } catch(e) { setError(e.message); }
    finally { setSavingFinance(false); }
  }

  function buildAppelFonds() {
    const nom  = [dossier.titre, dossier.prenom, dossier.nom_voyageur].filter(Boolean).join(' ');
    const solde = (parseFloat(montantTotal||0) - parseFloat(acomptePaye||0));
    const fmt  = v => v.toLocaleString('fr-FR', {minimumFractionDigits:2}) + ' €';
    return `Madame, Monsieur ${nom},\n\nNous vous contactons dans le cadre du traitement de votre dossier d'indemnisation.\n\nSuite à l'analyse de votre dossier (N° ${dossier.numero_dossier}), nous vous informons que le montant de votre indemnisation est le suivant :\n\n• Montant total du voyage : ${fmt(parseFloat(montantTotal||0))}\n• Acompte déjà versé : ${fmt(parseFloat(acomptePaye||0))}\n• Solde restant dû : ${fmt(solde)}\n\nNous reviendrons vers vous prochainement pour vous informer des modalités de versement.\n\nCordialement,\nL'équipe Arcus Solutions`;
  }

  function copierAppelFonds() {
    navigator.clipboard.writeText(buildAppelFonds()).then(() => {
      setAppelCopied(true); setTimeout(() => setAppelCopied(false), 2000);
    });
  }

  function genRef(index) {
    const pad = String(index + 1).padStart(3, '0');
    return `ARCUS-${dossier?.numero_procedure || ''}-${dossier?.numero_dossier || ''}-PAY-${pad}`;
  }

  function addPaiement() {
    const newP = {
      id: Date.now(),
      type: 'particulier', nom:'', prenom:'', adresse:'', cp:'', ville:'',
      iban:'', swift:'', montant:'', reference:'', editing: true,
    };
    const updated = [...paiements, { ...newP, reference: genRef(paiements.length) }];
    setPaiements(updated);
  }

  function updatePaiement(id, field, value) {
    setPaiements(prev => prev.map(p => p.id===id ? {...p, [field]:value} : p));
  }

  function deletePaiement(id) {
    const updated = paiements.filter(p => p.id!==id)
      .map((p, i) => ({ ...p, reference: p.reference || genRef(i) }));
    setPaiements(updated);
    savePaiements(updated);
  }

  async function savePaiements(list) {
    const data = (list || paiements).map(p => {
      const { editing, ...rest } = p;
      return rest;
    });
    setSavingPaiement(true);
    try {
      const res = await authFetch(`${BASE}/sinistres/${procedureId}/dossiers/${dossierId}`, {
        method:'PUT', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ paiements: data }),
      });
      const d = await res.json();
      if (d.error) { setError(d.error); return; }
      setPaiements(Array.isArray(d.paiements) ? d.paiements : data);
    } catch(e) { setError(e.message); }
    finally { setSavingPaiement(false); }
  }

  function closeEditPaiement(id) {
    setPaiements(prev => prev.map(p => p.id===id ? {...p, editing:false} : p));
    savePaiements();
  }

  // Phase D4b : confirmer la recherche de sanction d'un paiement (verrouille la case)
  function confirmSanction(id) {
    const updated = paiements.map(p => p.id===id
      ? { ...p, sanction_done:true, sanction_at:new Date().toISOString(), sanction_check_input:undefined }
      : p);
    setPaiements(updated);
    savePaiements(updated);
  }

  async function saveDateLimite(val) {
    setDateLimite(val);
    try {
      await authFetch(`${BASE}/sinistres/${procedureId}/dossiers/${dossierId}`, {
        method:'PUT', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ date_limite_paiement: val || null }),
      });
      setDossier(prev => ({ ...prev, date_limite_paiement: val }));
    } catch(e) { setError(e.message); }
  }

  async function downloadPdf() {
    setPdfLoading(true);
    try {
      const token = localStorage.getItem('arcus_token');
      const res = await fetch(`${BASE}/sinistres/${procedureId}/dossiers/${dossierId}/appel-fonds-pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setError(err.error || 'Erreur génération PDF');
        return;
      }
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `appel-fonds-${dossier.numero_dossier}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch(e) { setError(e.message); }
    finally { setPdfLoading(false); }
  }

  async function envoyerPdfEmail() {
    const nom = [dossier.titre, dossier.prenom, dossier.nom_voyageur].filter(Boolean).join(' ');
    const solde = dossier.montant_total != null
      ? (dossier.montant_total - (dossier.acompte_paye||0)).toLocaleString('fr-FR', {minimumFractionDigits:2}) + ' €'
      : '—';
    const dlim = dossier.date_limite_paiement
      ? new Date(dossier.date_limite_paiement).toLocaleDateString('fr-FR') : '—';
    const body = encodeURIComponent(
      `Madame, Monsieur ${nom},\n\nVeuillez trouver ci-joint notre appel de fonds (N° ${dossier.numero_dossier}).\n\nSolde restant dû : ${solde}\nDate limite de paiement : ${dlim}\n\nCordialement,\nL'équipe Arcus Solutions`
    );
    window.open(`mailto:${dossier.email_voyageur||''}?subject=${encodeURIComponent(`Appel de fonds — ${dossier.numero_dossier}`)}&body=${body}`, '_blank');
  }

  async function saveSouhait(val) {
    setSouhaitVoyageur(val);
    try {
      const res = await authFetch(`${BASE}/sinistres/${procedureId}/dossiers/${dossierId}`, {
        method:'PUT', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ souhait_voyageur: val }),
      });
      const d = await res.json();
      if (!d.error) setDossier(prev => ({ ...prev, souhait_voyageur: val }));
    } catch(e) { setError(e.message); }
  }

  // Point 3 : enregistrer le souhait IMMÉDIATEMENT au clic, pour que la liste des
  // pièces justificatives (qui lit le souhait côté serveur via /docs/init) se
  // recalcule et se rafraîchisse visuellement sans attendre l'enregistrement manuel.
  async function saveSouhaitClient(val) {
    setSouhaitClient(val);
    try {
      const res = await authFetch(`${BASE}/sinistres/${procedureId}/dossiers/${dossierId}`, {
        method:'PUT', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ souhait_client: val }),
      });
      const d = await res.json();
      if (!d.error) setDossier(prev => ({ ...prev, souhait_client: val }));
    } catch(e) { setError(e.message); }
  }

  async function createVoyageurAcces() {
    if (!voyageurEmail.trim() || !voyageurPwd.trim()) return;
    setVoyageurSaving(true); setVoyageurError('');
    try {
      const res = await authFetch(`${BASE}/sinistres/${procedureId}/dossiers/${dossierId}/creer-voyageur`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ email: voyageurEmail, password: voyageurPwd }),
      });
      const data = await res.json();
      if (data.error) { setVoyageurError(data.error); return; }
      setVoyageurCreated(data);
    } catch(e) { setVoyageurError(e.message); }
    finally { setVoyageurSaving(false); }
  }

  function genPassword() {
    const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#';
    return Array.from({length:10}, ()=>chars[Math.floor(Math.random()*chars.length)]).join('');
  }

  async function changerStatut(newStatut) {
    setChangingStatut(true);
    try {
      const res  = await authFetch(`${BASE}/sinistres/${procedureId}/dossiers/${dossierId}/statut`, {
        method:'PATCH', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ statut: newStatut }),
      });
      const data = await res.json();
      if (data.error) { setError(data.error); return; }
      setDossier(prev => ({ ...prev, statut: data.statut }));
    } catch(e) { setError(e.message); }
    finally { setChangingStatut(false); }
  }

  // ── Phase B : transitions de workflow dédiées ──────────────
  async function transitionDossier(endpoint, body, successMsg) {
    setChangingStatut(true);
    setError('');
    try {
      const opts = { method:'POST', headers:{'Content-Type':'application/json'} };
      if (body) opts.body = JSON.stringify(body);
      const res = await authFetch(`${BASE}/sinistres/${procedureId}/dossiers/${dossierId}${endpoint}`, opts);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Erreur');
      }
      const data = await res.json();
      setDossier(data);
    } catch(e) { setError(e.message); }
    finally { setChangingStatut(false); }
  }
  const envoyerValidation = () => transitionDossier('/envoyer-validation');
  const validerDossier    = () => transitionDossier('/valider');
  const refuserDossier    = (motif) => transitionDossier('/refuser', { motif });
  const marquerPaye       = () => transitionDossier('/marquer-paye');
  const soumettreDossier  = () => transitionDossier('/soumettre');
  const declinerDossier   = (motif) => transitionDossier('/decliner', { motif });

  // ── Points 1 & 2 : champs bénéficiaire obligatoires + blocage soumission ──
  // Tous les champs de la section "Informations du bénéficiaire" sont obligatoires
  // SAUF "voyageurs concernés". On calcule la liste de ce qui manque pour la
  // signaler au voyageur et bloquer le bouton de soumission.
  const champsObligatoires = [
    { ok: !!(titre && String(titre).trim()),          label: t('sinistres.voyageur_titre') },
    { ok: !!(prenom && String(prenom).trim()),        label: t('sinistres.voyageur_prenom') },
    { ok: !!(nomV && String(nomV).trim()),            label: t('sinistres.voyageur_nom') },
    { ok: !!(email && String(email).trim()),          label: t('sinistres.voyageur_email') },
    { ok: !!(telephone && String(telephone).trim()),  label: t('sinistres.voyageur_telephone') },
    { ok: !!(dateDepart && String(dateDepart).trim()),       label: t('sinistres.benef_date_depart') },
    { ok: !!(montantFacture && String(montantFacture).trim()), label: t('sinistres.benef_montant_facture') },
    { ok: !!(typeReglement && String(typeReglement).trim()),   label: t('sinistres.benef_type_reglement') },
    { ok: Array.isArray(modesPaiementClient) && modesPaiementClient.length > 0, label: t('sinistres.benef_modes_paiement') },
    { ok: !!(souhaitClient && String(souhaitClient).trim()), label: t('sinistres.benef_souhait_client') },
  ];
  const champsManquants = champsObligatoires.filter(c => !c.ok).map(c => c.label);
  const benefComplete = champsManquants.length === 0;

  // Pièces non encore fournies (statut 'attente' = pas de fichier déposé).
  // Une pièce 'uploade', 'accepte' ou même 'refuse' (re-déposable) compte comme à traiter
  // si elle est en 'attente' ou 'refuse' → on demande au voyageur de (re)fournir.
  const piecesManquantes = piecesList
    .filter(p => p.statut === 'attente' || p.statut === 'refuse')
    .map(p => p.nom || p.libelle || t('sinistres.piece_label_generic'));

  // Le voyageur peut soumettre si tous les champs obligatoires sont remplis
  // ET qu'il ne reste aucune pièce à fournir.
  const peutSoumettre = benefComplete && piecesManquantes.length === 0;


  // Admin : passe le dossier en cours de traitement (réutilise /soumettre côté backend).
  async function startProcessing() {
    setStartingProcessing(true);
    await soumettreDossier();
    setStartingProcessing(false);
    setShowStartProcessing(false);
  }

  // ── Validation administrative (sur la forme) ──
  async function validerAdmin() {
    setAdminValidating(true); setError('');
    try {
      const res = await authFetch(`${BASE}/sinistres/${procedureId}/dossiers/${dossierId}/valider-admin`, { method:'POST' });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Erreur'); return; }
      setDossier(prev => ({ ...prev, ...data }));
      setAdminValide(true);
    } catch(e) { setError(e.message); }
    finally { setAdminValidating(false); }
  }
  async function annulerAdmin() {
    setAdminValidating(true); setError('');
    try {
      const res = await authFetch(`${BASE}/sinistres/${procedureId}/dossiers/${dossierId}/annuler-admin`, { method:'POST' });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Erreur'); return; }
      setDossier(prev => ({ ...prev, ...data }));
      setAdminValide(false);
    } catch(e) { setError(e.message); }
    finally { setAdminValidating(false); }
  }

  // ── Paiements reçus du solde (liste JSONB paiements_solde) ──
  // Enregistrement = sauvegarde + verrouillage des champs (avec confirmation).
  async function lockSolde() {
    setSavingSolde(true); setError('');
    try {
      const res = await authFetch(`${BASE}/sinistres/${procedureId}/dossiers/${dossierId}/lock-solde`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ paiements_solde: paiementsSolde }),
      });
      const d = await res.json();
      if (d.error) { setError(d.error); return; }
      setPaiementsSolde(Array.isArray(d.paiements_solde) ? d.paiements_solde : paiementsSolde);
      setSoldeLocked(true);
    } catch(e) { setError(e.message); }
    finally { setSavingSolde(false); setShowSoldeSaveConfirm(false); }
  }
  async function unlockSolde() {
    setSavingSolde(true); setError('');
    try {
      const res = await authFetch(`${BASE}/sinistres/${procedureId}/dossiers/${dossierId}/unlock-solde`, { method:'POST' });
      const d = await res.json();
      if (d.error) { setError(d.error); return; }
      setSoldeLocked(false);
    } catch(e) { setError(e.message); }
    finally { setSavingSolde(false); }
  }
  async function confirmerSolde() {
    setSavingSolde(true); setError('');
    try {
      const res = await authFetch(`${BASE}/sinistres/${procedureId}/dossiers/${dossierId}/confirmer-solde`, { method:'POST' });
      const d = await res.json();
      if (d.error) { setError(d.error); return; }
      setSoldeConfirme(true);
      setDossier(prev => ({ ...prev, ...d }));
    } catch(e) { setError(e.message); }
    finally { setSavingSolde(false); setShowSoldeRecuConfirm(false); }
  }
  async function annulerSolde() {
    setSavingSolde(true); setError('');
    try {
      const res = await authFetch(`${BASE}/sinistres/${procedureId}/dossiers/${dossierId}/annuler-solde`, { method:'POST' });
      const d = await res.json();
      if (d.error) { setError(d.error); return; }
      setSoldeConfirme(false);
    } catch(e) { setError(e.message); }
    finally { setSavingSolde(false); }
  }
  function addPaiementSolde() {
    setPaiementsSolde(prev => [...prev, {
      id: 'ps-' + Date.now() + '-' + Math.random().toString(36).slice(2,6),
      date_reception: '', emetteur: '', montant: '',
    }]);
  }
  function updatePaiementSolde(id, field, value) {
    setPaiementsSolde(prev => prev.map(p => p.id===id ? { ...p, [field]:value } : p));
  }
  async function deletePaiementSolde(id) {
    const updated = paiementsSolde.filter(p => p.id !== id);
    setPaiementsSolde(updated);
    // Persiste la suppression immédiatement (sans verrouiller)
    try {
      await authFetch(`${BASE}/sinistres/${procedureId}/dossiers/${dossierId}`, {
        method:'PUT', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ paiements_solde: updated }),
      });
    } catch(e) { /* silencieux */ }
  }
  // Totaux du solde
  const soldeDu = (() => {
    const tot = parseFloat(dossier?.montant_total || 0);
    const ac  = parseFloat(dossier?.acompte_paye || 0);
    return Math.max(0, tot - ac);
  })();
  const totalRecuSolde = paiementsSolde.reduce((s,p) => s + (parseFloat(p.montant) || 0), 0);
  const resteARecevoir = Math.max(0, soldeDu - totalRecuSolde);

  // ── Phase D4 : verrous de sections + sanctions screening ──
  async function toggleLock(section, locked) {
    setLockBusy(true);
    setError('');
    try {
      const res = await authFetch(`${BASE}/sinistres/${procedureId}/dossiers/${dossierId}/lock-section`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ section, locked }),
      });
      if (!res.ok) { const e = await res.json().catch(()=>({})); throw new Error(e.error || 'Erreur'); }
      const d = await res.json();
      setBenefLocked(!!d.infos_beneficiaire_locked);
      setVoyageLocked(!!d.infos_voyage_locked);
      setPaiementsLocked(!!d.paiements_locked);
      if (section === 'beneficiaire' && locked) setEditVoyageur(false);
      if (section === 'voyage' && locked) setEditFinance(false);
    } catch(e) { setError(e.message); }
    finally { setLockBusy(false); }
  }

  async function doSanctionCheck() {
    setLockBusy(true);
    setError('');
    try {
      const res = await authFetch(`${BASE}/sinistres/${procedureId}/dossiers/${dossierId}/sanction-check`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ date_naissance: dateNaissance || null }),
      });
      if (!res.ok) { const e = await res.json().catch(()=>({})); throw new Error(e.error || 'Erreur'); }
      const d = await res.json();
      setSanctionDone(true);
      setPaiementsLocked(true);
    } catch(e) { setError(e.message); }
    finally { setLockBusy(false); }
  }

  async function undoSanctionCheck() {
    setLockBusy(true);
    setError('');
    try {
      const res = await authFetch(`${BASE}/sinistres/${procedureId}/dossiers/${dossierId}/sanction-uncheck`, {
        method:'POST', headers:{'Content-Type':'application/json'},
      });
      if (!res.ok) { const e = await res.json().catch(()=>({})); throw new Error(e.error || 'Erreur'); }
      setSanctionDone(false);
      setPaiementsLocked(false);
    } catch(e) { setError(e.message); }
    finally { setLockBusy(false); }
  }

  // ── Phase D4 : helpers de validation conditionnelle ──
  // Souhait normalisé (le bénéficiaire utilise 'maintien', le voyage 'maintien_voyage')
  const souhaitNorm = (() => {
    const s = souhaitClient || dossier?.souhait_client || souhaitVoyageur || dossier?.souhait_voyageur || '';
    if (s === 'maintien_voyage') return 'maintien';
    return s;
  })();
  const isRemboursement = souhaitNorm === 'remboursement';
  const isMaintien      = souhaitNorm === 'maintien';
  const isRapatriement  = souhaitNorm === 'rapatriement';

  // L'appel de fonds est-il marqué payé ? (document catégorie appel_fonds)
  const appelFondsPaye = !!appelFondsDoc?.paye;

  // Les paiements pro ont-ils tous une facture ? (mode maintien/rapatriement)
  const paiementsProOntFacture = (() => {
    if (isRemboursement) return true; // pas de facture requise pour remboursement
    if (isMaintien) {
      return prestairesDossier.length > 0 && prestairesDossier.every(p => {
        const rowSaved = p.id && !String(p.id).startsWith('new-');
        return rowSaved && facturesParPaiement.some(f => String(f.paiement_lie) === String(p.id));
      });
    }
    if (isRapatriement) {
      const pros = paiements.filter(p => p.type === 'professionnel');
      if (pros.length === 0) return false;
      return pros.every(p => facturesParPaiement.some(f => String(f.paiement_lie) === String(p.id)));
    }
    return true;
  })();

  // Phase D4b : la recherche de sanction est faite PAR PAIEMENT.
  // Condition : tous les paiements doivent avoir leur sanction confirmée.
  const sanctionOk = paiements.length > 0 && paiements.every(p => p.sanction_done);

  // Au moins un prestataire enregistré (ligne sauvegardée = id réel) avec une
  // facture uploadée. La facture est liée à l'id de la LIGNE de paiement
  // (dossier_prestataires.id), pas au prestataire_id.
  const auMoinsUnPrestataireAvecFacture = prestairesDossier.some(p => {
    const rowSaved = p.id && !String(p.id).startsWith('new-');
    return p.prestataire_id && rowSaved &&
      facturesParPaiement.some(f => String(f.paiement_lie) === String(p.id));
  });

  // Conditions finales pour afficher "Envoi pour validation"
  const canRequestValidation = (() => {
    if (isMaintien) {
      // Maintien : dès qu'au moins un prestataire est enregistré avec sa facture uploadée.
      return auMoinsUnPrestataireAvecFacture;
    }
    // Remboursement / rapatriement : logique existante (pièces approuvées, paiements verrouillés, sanctions).
    if (!paiementsLocked) return false;
    if (!docsAllApproved) return false;
    if (!sanctionOk) return false;
    if (isRemboursement) return true;
    if (isRapatriement)  return paiementsProOntFacture;
    return false;
  })();

  // ── Phase C : Gestion des prestataires liés (mode maintien) ──
  function addPrestataireRow() {
    setPrestairesDossier(prev => [...prev, {
      id: 'new-' + Date.now() + '-' + Math.random().toString(36).slice(2,6),
      prestataire_id: '',
      montant: 0,
      reference: '',
      description: '',
      isNew: true,
    }]);
  }
  function updatePrestataireRow(idx, field, value) {
    setPrestairesDossier(prev => prev.map((p, i) => {
      if (i !== idx) return p;
      const next = { ...p, [field]: value };
      // Si on change le prestataire sélectionné, on copie ses infos bancaires pour affichage
      if (field === 'prestataire_id') {
        const ref = allPrestataires.find(ap => ap.id === value);
        if (ref) {
          next.prestataire_nom = ref.nom;
          next.iban = ref.iban;
          next.swift = ref.swift;
          next.numero_compte = ref.numero_compte;
          next.ville = ref.ville;
          next.pays = ref.pays;
        }
      }
      return next;
    }));
  }
  function removePrestataireRow(idx) {
    setPrestairesDossier(prev => prev.filter((_, i) => i !== idx));
  }
  async function savePrestataires() {
    setSavingPrestataires(true);
    setError('');
    try {
      const items = prestairesDossier
        .filter(p => p.prestataire_id)  // exclure les lignes sans sélection
        .map(p => ({
          // On transmet l'id réel (UUID) des lignes déjà enregistrées pour préserver
          // le lien des factures ; les lignes neuves (id 'new-…') n'ont pas d'id.
          id: (p.id && !String(p.id).startsWith('new-')) ? p.id : undefined,
          prestataire_id: p.prestataire_id,
          montant: Number(p.montant) || 0,
          reference: p.reference || null,
          description: p.description || null,
        }));
      const res = await authFetch(`${BASE}/sinistres/${procedureId}/dossiers/${dossierId}/prestataires`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || t('common.error'));
      }
      const data = await res.json();
      setPrestairesDossier(data.prestataires || []);
      // Re-fetch du dossier depuis le serveur pour confirmer la persistance réelle
      const fresh = await authFetch(`${BASE}/sinistres/${procedureId}/dossiers/${dossierId}`).then(r => r.json());
      if (fresh && !fresh.error) {
        setDossier(fresh);
        setPrestairesDossier(Array.isArray(fresh.prestataires) ? fresh.prestataires : (data.prestataires || []));
      }
      setSuccessMsg(t('common.saved') || 'Enregistré');
      setTimeout(() => setSuccessMsg(''), 2500);
    } catch (e) {
      setError(e.message);
    } finally {
      setSavingPrestataires(false);
    }
  }

  // ── Phase D4 : upload d'une facture liée à un paiement/prestataire ──
  async function uploadFacture(paiementLie, file, label) {
    if (!file) return;
    if (!paiementLie || String(paiementLie).startsWith('new-')) {
      setError(t('phase_d4_facture.save_row_first'));
      return;
    }
    setError('');
    try {
      // 0. Supprimer une éventuelle facture déjà liée à CETTE ligne de paiement (remplacement)
      const existing = facturesParPaiement.filter(f => String(f.paiement_lie) === String(paiementLie));
      for (const ex of existing) {
        await authFetch(`${BASE}/docs-dossier/${ex.id}`, { method:'DELETE' }).catch(() => {});
      }
      // 1. Créer le document facture (catégorie 'facture', lié à la ligne de paiement)
      const createRes = await authFetch(`${BASE}/dossiers/${dossierId}/docs`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ nom: label || `Facture ${file.name}`, categorie: 'facture', paiement_lie: paiementLie }),
      });
      if (!createRes.ok) throw new Error(t('common.error'));
      const doc = await createRes.json();
      // 2. Uploader le fichier
      const fd = new FormData();
      fd.append('fichier', file);
      const token = localStorage.getItem('arcus_token');
      const upRes = await fetch(`${BASE}/docs-dossier/${doc.id}/upload`, {
        method:'POST',
        headers: token ? { Authorization:`Bearer ${token}` } : {},
        body: fd,
      });
      if (!upRes.ok) throw new Error(t('common.error'));
      await reloadDocsStatus();
    } catch(e) { setError(e.message); }
  }
  // Ouvre la facture uploadée dans un nouvel onglet (récupération authentifiée en blob)
  async function ouvrirFacture(fac) {
    try {
      const token = localStorage.getItem('arcus_token');
      const res = await fetch(`${BASE}/docs-dossier/${fac.id}/fichier`, {
        headers: token ? { Authorization:`Bearer ${token}` } : {},
      });
      if (!res.ok) { setError(t('common.error')); return; }
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      // Libère l'URL après un délai (le temps que l'onglet l'ouvre)
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch(e) { setError(e.message); }
  }
  // Trouve la facture liée à un paiement donné (si déjà uploadée)
  const factureFor = (paiementLie) => facturesParPaiement.find(f => String(f.paiement_lie) === String(paiementLie));

  if (loading) return <div style={{padding:24,color:'#94A3B8'}}>Chargement…</div>;
  if (error && !dossier) return (
    <div style={{padding:24}}>
      <div style={{color:'#DC2626',marginBottom:12}}>⚠ {error}</div>
      {!isVoyageur && (
        <button onClick={() => navigate(`/sinistres/${procedureId}`)}
          style={{fontSize:13,padding:'6px 14px',borderRadius:6,border:'0.5px solid #E6E9ED',cursor:'pointer'}}>
          ← Retour à la procédure
        </button>
      )}
    </div>
  );
  if (!dossier) return null;

  const st = STATUTS_DOSSIER[dossier.statut] || STATUTS_DOSSIER.en_attente_informations;

  return (
    <div>
      {/* ── En-tête de fiche (style "Ligne") ───────────────────── */}
      {!isVoyageur && (
        <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:12,
          color:theme.textTertiary, marginBottom:10 }}>
          <span onClick={() => navigate(`/sinistres/${procedureId}`)}
            style={{ color:ORANGE, cursor:'pointer', display:'inline-flex', alignItems:'center', gap:4 }}>
            <SvgIcon size={14} color={ORANGE}><path d="M15 6l-6 6l6 6"/></SvgIcon>
            {dossier.agence_nom || t('sinistres.dossiers_title')}
          </span>
          <span>/</span>
          <span>{dossier.numero_procedure}</span>
        </div>
      )}
      <div style={{ ...pageTitleStyle, marginBottom:20, alignItems:'center' }}>
        <div style={{ width:42, height:42, borderRadius:'50%', background:theme.sidebarBg,
          display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0,
          color:ORANGE, fontSize:15, fontWeight:500 }}>
          {(dossier.nom_voyageur?.[0] || dossier.numero_dossier?.[0] || '?').toUpperCase()}
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:22, fontWeight:800, color:theme.text, lineHeight:1.15 }}>
            {dossier.nom_voyageur || dossier.numero_dossier}
          </div>
          <div style={{ fontSize:12.5, color:theme.textSecondary, marginTop:2 }}>
            {TYPES[dossier.type_voyageur]} · <span style={{ fontFamily:'monospace' }}>{dossier.numero_dossier}</span>
          </div>
        </div>
        <span style={statutBadgeStyle(dossier.statut)}>
          <span style={{ width:7,height:7,borderRadius:'50%',background:(theme.statut[dossier.statut]||theme.statut.en_attente_informations).dot }}/>
          {st.label}
        </span>
      </div>

      {error && (
        <div style={{ background:'#FEE2E2', border:'1px solid #FCA5A5', borderRadius:7,
          padding:'9px 14px', marginBottom:14, fontSize:13, color:'#991B1B' }}>⚠ {error}</div>
      )}

      {/* Bandeau : dossier verrouillé en attente de validation manager */}
      {validationLock && (
        <div style={{ background:'#F5F3FF', border:'1px solid #C4B5FD', borderRadius:10,
          padding:'12px 16px', marginBottom:16, display:'flex', alignItems:'center', gap:10 }}>
          <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="#5B21B6" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"
            dangerouslySetInnerHTML={{ __html: sectionIconPaths.lock }} />
          <div style={{ fontSize:13, color:'#5B21B6', fontWeight:600 }}>
            {t('phase_solde.validation_lock_banner')}
          </div>
        </div>
      )}

      {/* ── Phase D3 : Message d'accueil voyageur ───────────────── */}
      {isVoyageur && (
        <div style={{ background:'linear-gradient(135deg, #FEF3E2 0%, #FFF9F0 100%)',
          border:'1px solid #F3C98B', borderRadius:10, padding:'16px 20px', marginBottom:16 }}>
          <div style={{ fontSize:15, fontWeight:700, color:'#9A5A00', marginBottom:6 }}>
            {t('voyageur_intro.title')}
          </div>
          <div style={{ fontSize:13, color:'#7C4A03', lineHeight:1.5 }}>
            {t('voyageur_intro.body')}
          </div>
        </div>
      )}

      {/* ── Statut du dossier (lecture seule — piloté par les actions) ───── */}
      {!isVoyageur && (
      <div style={sectionCardStyle}>
        <SectionTitle icon="status">{t('sinistres.statut_dossier')}</SectionTitle>
        <div style={{ marginTop:14 }}>
          <span style={{ display:'inline-flex', alignItems:'center', gap:8,
            background:st.bg, color:st.color, borderRadius:99, padding:'8px 18px', fontSize:14, fontWeight:600 }}>
            <span style={{ width:9,height:9,borderRadius:'50%',background:st.dot }}/>
            {t('sinistres.dos_statut_' + dossier.statut)}
          </span>
        </div>
      </div>
      )}

      {/* ── Informations du bénéficiaire ─────────────────────── */}
      <div style={sectionCardStyle}>
        <div style={sectionHeaderRow}>
          <SectionTitle icon="beneficiary" locked={benefLocked} lockedLabel={t('phase_d4.locked')}>
            {t('sinistres.voyageur_info')}
          </SectionTitle>
          {isArcus && !isReadOnly && !benefLocked && (
            <button onClick={()=>{ setShowVoyageurModal(true); setVoyageurCreated(null); setVoyageurError(''); setVoyageurPwd(genPassword()); }}
              style={{ fontSize:12, padding:'7px 13px', borderRadius:theme.radius, border:'1px solid #14B8A6',
                color:'#0F766E', background:'#F0FDFA', cursor:'pointer', fontWeight:500,
                display:'inline-flex', alignItems:'center', gap:5, flexShrink:0 }}>
              <SvgIcon size={15} color="#0F766E"><path d="M16.555 3.843l3.602 3.602a2.877 2.877 0 0 1 0 4.069l-2.643 2.643a2.877 2.877 0 0 1 -4.069 0l-.301 -.301l-6.558 6.558a2 2 0 0 1 -1.239 .578l-.175 .007h-1.977a1 1 0 0 1 -.993 -.883l-.007 -.117v-1.977a2 2 0 0 1 .467 -1.284l.119 -.13l.414 -.414h2v-2h2v-2l2.144 -2.144l-.301 -.301a2.877 2.877 0 0 1 0 -4.069l2.643 -2.643a2.877 2.877 0 0 1 4.069 0zM15 9h.01"/></SvgIcon>
              {t('sinistres.creer_acces_voyageur')}
            </button>
          )}
        </div>
        <div style={sectionIntroStyle}>{t('sinistres.benef_intro')}</div>

        {/* Phase D6-fix : section bénéficiaire toujours dépliée (plus de toggle) */}
        {true ? (
          <>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10, marginBottom:10 }}>
              <div>
                <label style={lbl}>{t('sinistres.voyageur_titre')}<Req/></label>
                <select value={titre} onChange={e=>setTitre(e.target.value)} disabled={benefLocked||isReadOnly} style={inp}>
                  <option value="">—</option>
                  {[['M',t('sinistres.voyageur_titre_m')],
                    ['Mme',t('sinistres.voyageur_titre_mme')],
                    ['M/Mme',t('sinistres.voyageur_titre_mmme')],
                    ['Entreprise',t('sinistres.voyageur_titre_ste')],
                    ['Association',t('sinistres.voyageur_titre_asso')]
                  ].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div>
                <label style={lbl}>{t('sinistres.voyageur_nom')}<Req/></label>
                <input value={nomV} onChange={e=>setNomV(e.target.value)} disabled={benefLocked||isReadOnly} style={inp}/>
              </div>
              <div>
                <label style={lbl}>{t('sinistres.voyageur_prenom')}<Req/></label>
                <input value={prenom} onChange={e=>setPrenom(e.target.value)} disabled={benefLocked||isReadOnly} style={inp}/>
              </div>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:14 }}>
              <div>
                <label style={lbl}>{t('sinistres.voyageur_email')}<Req/></label>
                <input type="email" value={email} onChange={e=>setEmail(e.target.value)} disabled={benefLocked||isReadOnly} style={inp}/>
              </div>
              <div>
                <label style={lbl}>{t('sinistres.voyageur_telephone')}<Req/></label>
                <input type="tel" value={telephone} onChange={e=>setTel(e.target.value)} disabled={benefLocked||isReadOnly} style={inp}/>
              </div>
            </div>

            {/* ── Qualification du sinistre ── */}
            <div style={{ borderTop:'1px solid #F1F5F9', marginBottom:14, paddingTop:14 }}>
              <div style={{ fontSize:12, fontWeight:700, color:'#64748B', marginBottom:12,
                textTransform:'uppercase', letterSpacing:'.05em' }}>
                Qualification du sinistre
              </div>
              {/* Date de départ du voyage */}
              <div style={{ marginBottom:12 }}>
                <label style={lbl}>{t('sinistres.benef_date_depart')}<Req/></label>
                <input type="date" value={dateDepart}
                  onChange={e=>setDateDepart(e.target.value)} disabled={benefLocked||isReadOnly}
                  style={{...inp, maxWidth:240, opacity:(benefLocked||isReadOnly)?0.7:1}}/>
              </div>
              {/* Montant facturé */}
              <div style={{ marginBottom:12 }}>
                <label style={lbl}>{t('sinistres.benef_montant_facture')}<Req/></label>
                <div style={{ position:'relative', maxWidth:240 }}>
                  <input type="number" value={montantFacture}
                    onChange={e=>setMontantFacture(e.target.value)}
                    placeholder="0.00" min="0" step="0.01" disabled={benefLocked||isReadOnly}
                    style={{...inp, paddingRight:24}}/>
                  <span style={{position:'absolute',right:8,top:'50%',transform:'translateY(-50%)',
                    fontSize:13,color:'#94A3B8',pointerEvents:'none'}}>€</span>
                </div>
              </div>
              {/* Acompte ou totalité */}
              <div style={{ marginBottom:12 }}>
                <label style={lbl}>{t('sinistres.benef_type_reglement')}<Req/></label>
                <div style={{ display:'flex', gap:8, marginTop:4 }}>
                  {[['acompte',t('sinistres.benef_type_acompte')],['totalite',t('sinistres.benef_type_totalite')]].map(([val,lbl2])=>(
                    <button key={val} type="button" onClick={()=>!(benefLocked||isReadOnly)&&setTypeReglement(val)} disabled={benefLocked||isReadOnly}
                      style={{ padding:'8px 16px', borderRadius:7, cursor:(benefLocked||isReadOnly)?'default':'pointer', fontSize:13,
                        fontWeight:typeReglement===val?700:400,
                        background:typeReglement===val?'#1A1A1A':'#F8FAFC',
                        color:typeReglement===val?'#fff':'#64748B',
                        border:`1.5px solid ${typeReglement===val?'#1A1A1A':'#E2E8F0'}` }}>
                      {lbl2}
                    </button>
                  ))}
                </div>
              </div>
              {/* Montant acompte — conditionnel */}
              {typeReglement === 'acompte' && (
                <div style={{ marginBottom:12 }}>
                  <label style={lbl}>{t('sinistres.benef_montant_acompte')}</label>
                  <div style={{ position:'relative', maxWidth:240 }}>
                    <input type="number" value={montantAcompteClient}
                      onChange={e=>setMontantAcompteClient(e.target.value)}
                      placeholder="0.00" min="0" step="0.01" disabled={benefLocked||isReadOnly}
                      style={{...inp, paddingRight:24}}/>
                    <span style={{position:'absolute',right:8,top:'50%',transform:'translateY(-50%)',
                      fontSize:13,color:'#94A3B8',pointerEvents:'none'}}>€</span>
                  </div>
                </div>
              )}
              {/* Modes de paiement — choix multiple */}
              <div style={{ marginBottom:12 }}>
                <label style={lbl}>{t('sinistres.benef_modes_paiement')}<Req/></label>
                <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginTop:4 }}>
                  {[
                    ['carte',    t('sinistres.benef_mode_carte')],
                    ['virement', t('sinistres.benef_mode_virement')],
                    ['cheque',   t('sinistres.benef_mode_cheque')],
                    ['especes',  t('sinistres.benef_mode_especes')],
                    ['cadeau',   t('sinistres.benef_mode_cadeau')],
                  ].map(([val,lbl2])=>{
                    const active = modesPaiementClient.includes(val);
                    return (
                      <button key={val} type="button" disabled={benefLocked||isReadOnly} onClick={()=>{
                        if (benefLocked||isReadOnly) return;
                        setModesPaiementClient(prev =>
                          active ? prev.filter(x=>x!==val) : [...prev, val]);
                      }}
                        style={{ padding:'8px 16px', borderRadius:7, cursor:(benefLocked||isReadOnly)?'default':'pointer', fontSize:13,
                          fontWeight:active?700:400,
                          background:active?'#1A1A1A':'#F8FAFC',
                          color:active?'#fff':'#64748B',
                          border:`1.5px solid ${active?'#1A1A1A':'#E2E8F0'}` }}>
                        {lbl2}
                      </button>
                    );
                  })}
                </div>
                {/* Message obligatoire si paiement par carte bancaire (article 75) */}
                {modesPaiementClient.includes('carte') && (
                  <div style={{ marginTop:10, padding:'10px 14px', borderRadius:8,
                    background:'#FEF2F2', border:'1px solid #FECACA', color:'#B91C1C',
                    fontSize:12.5, lineHeight:1.5 }}>
                    {t('sinistres.benef_carte_warning')}
                  </div>
                )}
              </div>
              {/* Voyageurs concernés — liste dynamique (nom + prénom) */}
              <div style={{ marginBottom:12 }}>
                <label style={lbl}>{t('sinistres.benef_voyageurs_label')}</label>
                {voyageursList.map((v, i) => (
                  <div key={i} style={{ display:'flex', gap:8, marginBottom:8, alignItems:'center' }}>
                    <input value={v.nom||''} placeholder={t('sinistres.voyageur_nom')}
                      onChange={e=>{ const next=[...voyageursList]; next[i]={...next[i],nom:e.target.value}; setVoyageursList(next); }}
                      disabled={benefLocked||isReadOnly}
                      style={{...inp, flex:1, opacity:(benefLocked||isReadOnly)?0.7:1}}/>
                    <input value={v.prenom||''} placeholder={t('sinistres.voyageur_prenom')}
                      onChange={e=>{ const next=[...voyageursList]; next[i]={...next[i],prenom:e.target.value}; setVoyageursList(next); }}
                      disabled={benefLocked||isReadOnly}
                      style={{...inp, flex:1, opacity:(benefLocked||isReadOnly)?0.7:1}}/>
                    {!benefLocked && !isReadOnly && (
                      <button type="button" onClick={()=>setVoyageursList(voyageursList.filter((_,j)=>j!==i))}
                        title={t('common.delete')}
                        style={{ width:34, height:34, borderRadius:7, flexShrink:0, cursor:'pointer',
                          background:'#FEE2E2', color:'#991B1B', border:'none',
                          display:'inline-flex', alignItems:'center', justifyContent:'center' }}>
                        <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="#991B1B" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M4 7h16M10 11v6M14 11v6M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3"/>
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
                {!benefLocked && !isReadOnly && (
                  <button type="button" onClick={()=>setVoyageursList([...voyageursList,{nom:'',prenom:''}])}
                    style={{ ...secondaryButtonStyle, padding:'8px 14px', marginTop:2 }}>
                    + {t('sinistres.benef_add_voyageur')}
                  </button>
                )}
                <div style={{ fontSize:12, color:theme.textSecondary, marginTop:8, fontStyle:'italic' }}>
                  {t('sinistres.benef_voyageurs_groupe')}
                </div>
              </div>
              {/* Souhait du client */}
              <div style={{ marginBottom:4 }}>
                <label style={lbl}>{t('sinistres.benef_souhait_client')}<Req/></label>
                <div style={{ display:'flex', gap:8, marginTop:4, flexWrap:'wrap' }}>
                  {[
                    ['remboursement', t('sinistres.benef_souhait_remboursement')],
                    ['maintien',      t('sinistres.benef_souhait_maintien')],
                    ['rapatriement',  t('sinistres.benef_souhait_rapatriement')],
                  ].map(([val,lbl2])=>(
                    <button key={val} type="button" disabled={benefLocked||isReadOnly} onClick={()=>!(benefLocked||isReadOnly)&&saveSouhaitClient(val)}
                      style={{ flex:1, padding:'8px 6px', borderRadius:8, cursor:(benefLocked||isReadOnly)?'default':'pointer',
                        fontSize:12, fontWeight:souhaitClient===val?700:400,
                        background:souhaitClient===val?'#1A1A1A':'#F8FAFC',
                        color:souhaitClient===val?'#fff':'#64748B',
                        border:`2px solid ${souhaitClient===val?'#1A1A1A':'#E2E8F0'}` }}>
                      {lbl2}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {!isReadOnly && (
            <div style={sectionFooterStyle}>
              {successMsg && <span style={{ fontSize:12, color:'#047857', fontWeight:600, alignSelf:'center', marginRight:'auto' }}>✓ {successMsg}</span>}
              {canLock && (
                <button onClick={() => toggleLock('beneficiaire', !benefLocked)} disabled={lockBusy}
                  style={lockButtonStyle(benefLocked, lockBusy)}>
                  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"
                    dangerouslySetInnerHTML={{ __html: benefLocked ? sectionIconPaths.unlock : sectionIconPaths.lock }} />
                  {benefLocked ? t('phase_d4.unlock') : t('phase_d4.lock')}
                </button>
              )}
              {!benefLocked && (
              <button onClick={saveVoyageur} disabled={!nomV.trim()||savingVoyageur}
                style={{ fontSize:13, padding:'8px 18px', borderRadius:theme.radius, background:theme.orange,
                  color:'#fff', border:'none', cursor:'pointer', fontWeight:500,
                  opacity:(!nomV.trim()||savingVoyageur)?0.5:1 }}>
                {savingVoyageur ? '…' : t('common.save')}
              </button>
              )}
              {/* Admin : passer le dossier en cours de traitement sans attendre le voyageur */}
              {!isVoyageur && isArcus && dossier.statut === 'en_attente_informations' && (
                <button onClick={()=>setShowStartProcessing(true)}
                  style={{ fontSize:13, padding:'8px 18px', borderRadius:theme.radius, border:'none',
                    background:'#1A1A1A', color:'#fff', cursor:'pointer', fontWeight:500,
                    display:'inline-flex', alignItems:'center', gap:6 }}>
                  <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M7 4v16l13 -8z"/></svg>
                  {t('sinistres.passer_en_cours')}
                </button>
              )}
            </div>
            )}
          </>
        ) : (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:12 }}>
            {[
              [t('sinistres.voyageur_titre'),     dossier.titre || '—'],
              [t('sinistres.voyageur_nom'),        dossier.nom_voyageur || '—'],
              [t('sinistres.voyageur_prenom'),     dossier.prenom || '—'],
              [t('sinistres.voyageur_email'),      dossier.email_voyageur || '—'],
              [t('sinistres.voyageur_telephone'),  dossier.telephone_voyageur || '—'],
            ].map(([label, value]) => (
              <div key={label}>
                <div style={{ fontSize:11, color:'#64748B', fontWeight:600, marginBottom:2 }}>{label.toUpperCase()}</div>
                <div style={{ fontSize:13, color:'#1A1A1A', fontWeight: value==='—'?400:600 }}>{value}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div id="voyage-paiements-bloc">
      {/* ── Informations relatives au voyage ─────────────────── */}
      <div style={sectionCardStyle}>
        <div style={sectionHeaderRow}>
          <SectionTitle icon="travel" locked={voyageLocked} lockedLabel={t('phase_d4.locked')}>
            {t('sinistres.voyage_info')}
          </SectionTitle>
        </div>
        {!isVoyageur && <div style={sectionIntroStyle}>{t('sinistres.voyage_intro')}</div>}

        {/* Date limite paiement — visible admin quand Maintien du voyage */}
        {!isVoyageur && souhaitVoyageur === 'maintien_voyage' && (
          <div style={{ background:'#EFF6FF', border:'1px solid #BFDBFE', borderRadius:8,
            padding:'12px 16px', marginBottom:16 }}>
            <div style={{ display:'flex', alignItems:'flex-start', gap:16, flexWrap:'wrap' }}>
              <div style={{ flex:1, minWidth:200 }}>
                <label style={{ fontSize:12, fontWeight:700, color:'#1D4ED8', display:'block', marginBottom:5 }}>
                  📅 {t('sinistres.date_limite_paiement')}
                </label>
                <input type="date" value={dateLimite}
                  onChange={e => saveDateLimite(e.target.value)}
                  disabled={isReadOnly || voyageLocked}
                  style={{ border:'1.5px solid #93C5FD', borderRadius:7, padding:'8px 12px',
                    fontSize:13, fontFamily:'inherit', color:'#1D4ED8', fontWeight:600,
                    opacity:(isReadOnly||voyageLocked)?0.6:1 }}/>
              </div>

            </div>
          </div>
        )}

        {(!isVoyageur && editFinance) ? (
          <>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
              <div>
                <label style={lbl}>{t('sinistres.voyage_montant_total')}</label>
                <div style={{ position:'relative' }}>
                  <input type="number" value={montantTotal} onChange={e=>setMontantTotal(e.target.value)}
                    placeholder="0.00" min="0" step="0.01"
                    style={{...inp, paddingRight:24}}/>
                  <span style={{position:'absolute',right:8,top:'50%',transform:'translateY(-50%)',
                    fontSize:13,color:'#94A3B8',pointerEvents:'none'}}>€</span>
                </div>
              </div>
              <div>
                <label style={{ ...lbl, color: souhaitVoyageur==='remboursement' ? '#059669' : '#374151', fontWeight:700 }}>
                  {t('sinistres.voyage_acompte')}
                  {souhaitVoyageur==='remboursement' && <span style={{marginLeft:6,fontSize:11}}>●</span>}
                </label>
                <div style={{ position:'relative' }}>
                  <input type="number" value={acomptePaye} onChange={e=>setAcomptePaye(e.target.value)}
                    placeholder="0.00" min="0" step="0.01"
                    style={{...inp, paddingRight:24,
                      border: souhaitVoyageur==='remboursement' ? '2px solid #059669' : inp.border,
                      fontWeight: souhaitVoyageur==='remboursement' ? 700 : 400}}/>
                  <span style={{position:'absolute',right:8,top:'50%',transform:'translateY(-50%)',
                    fontSize:13,color:'#94A3B8',pointerEvents:'none'}}>€</span>
                </div>
              </div>
            </div>
            {/* Solde calculé — vert si Maintien du voyage, neutre sinon */}
            <div style={{
              background: souhaitVoyageur==='maintien_voyage' ? '#F0FDF4' : '#F8FAFC',
              border: `1px solid ${souhaitVoyageur==='maintien_voyage' ? '#6EE7B7' : '#E2E8F0'}`,
              borderRadius:8, padding:'10px 14px', marginBottom:14,
              display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <div>
                <div style={{ fontSize:12, fontWeight:700, color: souhaitVoyageur==='maintien_voyage' ? '#065F46' : '#374151' }}>
                  {t('sinistres.voyage_solde')}
                  {souhaitVoyageur==='maintien_voyage' && <span style={{marginLeft:6,fontSize:11}}>●</span>}
                </div>
                <div style={{ fontSize:11, color: souhaitVoyageur==='maintien_voyage' ? '#6EE7B7' : '#94A3B8' }}>
                  {t('sinistres.voyage_solde_auto')}
                </div>
              </div>
              <div style={{ fontSize:20, fontWeight:800, color: souhaitVoyageur==='maintien_voyage' ? '#059669' : '#1A1A1A' }}>
                {(parseFloat(montantTotal||0) - parseFloat(acomptePaye||0))
                  .toLocaleString('fr-FR', {minimumFractionDigits:2})} €
              </div>
            </div>
            <div style={sectionFooterStyle}>
              {canLock && (
                <button onClick={() => toggleLock('voyage', !voyageLocked)} disabled={lockBusy}
                  style={lockButtonStyle(voyageLocked, lockBusy)}>
                  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"
                    dangerouslySetInnerHTML={{ __html: voyageLocked ? sectionIconPaths.unlock : sectionIconPaths.lock }} />
                  {voyageLocked ? t('phase_d4.unlock') : t('phase_d4.lock')}
                </button>
              )}
              <button onClick={() => { setEditFinance(false); setMontantTotal(dossier.montant_total!=null?String(dossier.montant_total):''); setAcomptePaye(dossier.acompte_paye!=null?String(dossier.acompte_paye):''); }}
                style={{ ...secondaryButtonStyle, padding:'8px 16px' }}>
                {t('common.cancel')}
              </button>
              <button onClick={saveFinance} disabled={savingFinance}
                style={{ fontSize:13, padding:'8px 18px', borderRadius:theme.radius, background:theme.orange,
                  color:'#fff', border:'none', cursor:'pointer', fontWeight:500, opacity:savingFinance?0.5:1 }}>
                {savingFinance ? '…' : t('common.save')}
              </button>
            </div>
          </>
        ) : (
          <>
          <VoyageLecture
            dossier={dossier}
            souhait={souhaitVoyageur}
            t={t}
            onAppelFonds={() => setShowAppelFonds(true)}
          />
          {!isVoyageur && (canLock || (!isReadOnly && !voyageLocked)) && (
            <div style={sectionFooterStyle}>
              {canLock && (
                <button onClick={() => toggleLock('voyage', !voyageLocked)} disabled={lockBusy}
                  style={lockButtonStyle(voyageLocked, lockBusy)}>
                  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"
                    dangerouslySetInnerHTML={{ __html: voyageLocked ? sectionIconPaths.unlock : sectionIconPaths.lock }} />
                  {voyageLocked ? t('phase_d4.unlock') : t('phase_d4.lock')}
                </button>
              )}
              {!isReadOnly && !voyageLocked && (
                <button onClick={() => setEditFinance(true)}
                  style={{ ...secondaryButtonStyle, padding:'8px 16px' }}>
                  {t('sinistres.modifier')}
                </button>
              )}
            </div>
          )}
          </>
        )}
      </div>

      {/* ── Modal Appel de fonds ──────────────────────────────── */}
      {showAppelFonds && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.45)', display:'flex',
          alignItems:'center', justifyContent:'center', zIndex:1200 }}>
          <div style={{ background:'#fff', borderRadius:12, padding:24, width:560, maxWidth:'92vw',
            maxHeight:'80vh', display:'flex', flexDirection:'column' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
              <div>
                <div style={{ fontSize:15, fontWeight:600, display:'flex', alignItems:'center', gap:8 }}><SvgIcon size={18} color={ORANGE}><path d="M14 3v4a1 1 0 0 0 1 1h4M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z"/></SvgIcon>{t('sinistres.voyage_appel_fonds_title')}</div>
                <div style={{ fontSize:11, color:'#64748B', marginTop:2 }}>
                  {t('sinistres.voyage_appel_fonds_soon')}
                </div>
              </div>
              <button onClick={() => setShowAppelFonds(false)}
                style={{ background:'none', border:'none', cursor:'pointer', fontSize:20, color:'#94A3B8' }}>✕</button>
            </div>
            <div style={{ fontSize:11, fontWeight:700, color:'#64748B', marginBottom:6,
              textTransform:'uppercase', letterSpacing:'.06em' }}>
              {t('sinistres.voyage_appel_fonds_preview')}
            </div>
            <pre style={{ flex:1, overflowY:'auto', background:'#F8FAFC', border:'0.5px solid #E6E9ED',
              borderRadius:7, padding:14, fontSize:12.5, whiteSpace:'pre-wrap', wordBreak:'break-word',
              fontFamily:'inherit', lineHeight:1.7, marginBottom:14 }}>
              {buildAppelFonds()}
            </pre>
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
              <button onClick={() => setShowAppelFonds(false)}
                style={{ fontSize:14, padding:'7px 16px', borderRadius:6, border:'1px solid #CBD5E1', cursor:'pointer', background:'#fff' }}>
                {t('common.close')}
              </button>
              <button onClick={copierAppelFonds}
                style={{ fontSize:14, padding:'7px 16px', borderRadius:6, border:'1px solid #CBD5E1',
                  cursor:'pointer', background:appelCopied?'#D1FAE5':'#fff',
                  color:appelCopied?'#065F46':'#1A1A1A', fontWeight:600 }}>
                {appelCopied ? t('sinistres.voyage_appel_fonds_copied') : t('sinistres.voyage_appel_fonds_copy')}
              </button>
              <button onClick={() => window.open(`mailto:${dossier.email_voyageur||''}?subject=${encodeURIComponent(t('sinistres.voyage_appel_fonds_title'))}&body=${encodeURIComponent(buildAppelFonds())}`, '_blank')}
                style={{ fontSize:14, padding:'7px 16px', borderRadius:6, background:'#F18A00',
                  color:'#fff', border:'none', cursor:'pointer', fontWeight:700 }}>
                📧 {t('sinistres.voyage_appel_fonds_email')}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>

      {/* ── Pièces justificatives ────────────────────────────── */}
      <div style={sectionCardStyle}>
        <DocsDossier dossierId={dossierId} souhaitClient={souhaitClient || dossier?.souhait_client || souhaitVoyageur || dossier?.souhait_voyageur} modesPaiement={modesPaiementClient} onDocsChange={reloadDocsStatus}/>

        {/* Validation du dossier administratif (sur la forme) — dans la même section que les pièces */}
        {!isVoyageur && (
          <div style={{ marginTop:16, paddingTop:16, borderTop:'1px solid #F1F5F9' }}>
            <div style={{ background: adminValide ? '#F0FDF4' : '#FAFAFA',
              border:`1px solid ${adminValide ? '#A7F3D0' : '#E2E8F0'}`, borderRadius:8, padding:14 }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:16, flexWrap:'wrap' }}>
                <div style={{ flex:1, minWidth:220 }}>
                  <div style={{ fontSize:14, fontWeight:700, color: adminValide ? '#047857' : '#1A1A1A',
                    display:'flex', alignItems:'center', gap:8 }}>
                    {adminValide && <span style={{ fontSize:16 }}>✓</span>}
                    {t('phase_solde.admin_valid_title')}
                  </div>
                  <div style={{ fontSize:12, color:'#64748B', marginTop:3 }}>
                    {adminValide ? t('phase_solde.admin_valid_done') : t('phase_solde.admin_valid_desc')}
                  </div>
                </div>
                {!isReadOnly && (
                  adminValide ? (
                    <button onClick={annulerAdmin} disabled={adminValidating}
                      style={{ fontSize:12, padding:'8px 14px', borderRadius:7, fontWeight:600,
                        background:'#fff', color:'#DC2626', border:'1px solid #FCA5A5',
                        cursor: adminValidating ? 'not-allowed' : 'pointer' }}>
                      {adminValidating ? '…' : t('phase_solde.admin_valid_cancel')}
                    </button>
                  ) : (
                    <button onClick={validerAdmin} disabled={adminValidating || !docsAllApproved}
                      title={!docsAllApproved ? t('phase_solde.admin_valid_need_docs') : ''}
                      style={{ fontSize:13, padding:'10px 18px', borderRadius:7, fontWeight:700,
                        background: docsAllApproved ? '#F18A00' : '#E2E8F0',
                        color: docsAllApproved ? '#fff' : '#94A3B8', border:'none',
                        cursor: (adminValidating || !docsAllApproved) ? 'not-allowed' : 'pointer' }}>
                      {adminValidating ? '…' : t('phase_solde.admin_valid_btn')}
                    </button>
                  )
                )}
              </div>
              {!adminValide && !docsAllApproved && (
                <div style={{ fontSize:12, color:'#92400E', background:'#FEF3C7', borderRadius:7,
                  padding:'8px 12px', marginTop:10 }}>
                  ⚠ {t('phase_solde.admin_valid_need_docs')}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Phase D3 : Bouton "Soumettre mon dossier" (voyageur) ── */}
      {isVoyageur && dossier.statut === 'en_attente_informations' && (
        <div style={{ background:'#fff', borderRadius:10, border:'2px solid #F18A00',
          padding:18, marginBottom:16 }}>
          <div style={{ display:'flex', alignItems:'center', gap:16 }}>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:14, fontWeight:700, color:'#9A5A00' }}>{t('voyageur_intro.submit_title')}</div>
              <div style={{ fontSize:12, color:'#7C4A03', marginTop:2 }}>{t('voyageur_intro.submit_desc')}</div>
            </div>
            <button onClick={() => setShowSubmitConfirm(true)} disabled={changingStatut || !peutSoumettre}
              title={!peutSoumettre ? t('sinistres.submit_blocked_hint') : ''}
              style={{ background: peutSoumettre ? '#F18A00' : '#E2E2DF', color: peutSoumettre ? '#fff' : '#9A9A95',
                border:'none', borderRadius:8, padding:'11px 22px', fontSize:14, fontWeight:700,
                cursor: (changingStatut || !peutSoumettre) ? 'not-allowed' : 'pointer', opacity: changingStatut ? .6 : 1 }}>
              {t('voyageur_intro.submit_btn')}
            </button>
          </div>
          {/* Liste de ce qui manque pour pouvoir soumettre */}
          {!peutSoumettre && (
            <div style={{ marginTop:14, paddingTop:14, borderTop:'1px solid #FDE9D5' }}>
              <div style={{ fontSize:12.5, fontWeight:700, color:'#C2410C', marginBottom:8 }}>
                {t('sinistres.submit_missing_title')}
              </div>
              {champsManquants.length > 0 && (
                <div style={{ marginBottom: piecesManquantes.length > 0 ? 8 : 0 }}>
                  <div style={{ fontSize:11.5, fontWeight:600, color:'#7C4A03', marginBottom:4 }}>
                    {t('sinistres.submit_missing_fields')}
                  </div>
                  <ul style={{ margin:0, paddingLeft:18, fontSize:12.5, color:'#7C4A03' }}>
                    {champsManquants.map((c, i) => <li key={i} style={{ marginBottom:2 }}>{c}</li>)}
                  </ul>
                </div>
              )}
              {piecesManquantes.length > 0 && (
                <div>
                  <div style={{ fontSize:11.5, fontWeight:600, color:'#7C4A03', marginBottom:4 }}>
                    {t('sinistres.submit_missing_pieces')}
                  </div>
                  <ul style={{ margin:0, paddingLeft:18, fontSize:12.5, color:'#7C4A03' }}>
                    {piecesManquantes.map((p, i) => <li key={i} style={{ marginBottom:2 }}>{p}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Phase D3 : Confirmation soumission ──────────────────── */}
      {showSubmitConfirm && (
        <div onClick={() => !changingStatut && setShowSubmitConfirm(false)}
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.5)',
            display:'flex', alignItems:'center', justifyContent:'center', zIndex:1200 }}>
          <div onClick={e=>e.stopPropagation()}
            style={{ background:'#fff', borderRadius:12, padding:24, width:'min(440px, calc(100% - 32px))' }}>
            <div style={{ fontSize:17, fontWeight:800, color:'#161616', marginBottom:6 }}>{t('voyageur_intro.submit_confirm_title')}</div>
            <div style={{ fontSize:13, color:'#475569', marginBottom:18 }}>{t('voyageur_intro.submit_confirm_desc')}</div>
            <div style={{ display:'flex', justifyContent:'flex-end', gap:10 }}>
              <button onClick={()=>setShowSubmitConfirm(false)} disabled={changingStatut}
                style={{ padding:'8px 16px', borderRadius:8, border:'1px solid #E5E7EB', background:'#fff', cursor:'pointer' }}>
                {t('common.cancel')}
              </button>
              <button onClick={async ()=>{ await soumettreDossier(); setShowSubmitConfirm(false); }}
                disabled={changingStatut}
                style={{ padding:'8px 16px', borderRadius:8, border:'none',
                  background:'#F18A00', color:'#fff', fontWeight:700,
                  cursor:changingStatut?'not-allowed':'pointer', opacity:changingStatut?.6:1 }}>
                {changingStatut ? t('common.saving') : t('voyageur_intro.submit_confirm_btn')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Phase D4 : Modal décliner le dossier ──────────────── */}
      {showDeclineModal && (
        <div onClick={() => !changingStatut && setShowDeclineModal(false)}
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.5)',
            display:'flex', alignItems:'center', justifyContent:'center', zIndex:1200 }}>
          <div onClick={e=>e.stopPropagation()}
            style={{ background:'#fff', borderRadius:12, padding:24, width:'min(460px, calc(100% - 32px))' }}>
            <div style={{ fontSize:17, fontWeight:800, color:'#161616', marginBottom:6 }}>{t('phase_d4.decline_modal_title')}</div>
            <div style={{ fontSize:13, color:'#64748B', marginBottom:16 }}>{t('phase_d4.decline_modal_desc')}</div>
            <textarea value={declineMotif} onChange={e=>setDeclineMotif(e.target.value)}
              placeholder={t('phase_d4.decline_motif_placeholder')}
              style={{ width:'100%', minHeight:90, padding:'10px 12px', borderRadius:8,
                border:'1px solid #D9D9D5', fontSize:13, fontFamily:'inherit', resize:'vertical', boxSizing:'border-box' }} />
            <div style={{ marginTop:16, display:'flex', justifyContent:'flex-end', gap:10 }}>
              <button onClick={()=>setShowDeclineModal(false)} disabled={changingStatut}
                style={{ padding:'8px 16px', borderRadius:8, border:'1px solid #E5E7EB', background:'#fff', cursor:'pointer' }}>
                {t('common.cancel')}
              </button>
              <button onClick={async ()=>{ await declinerDossier(declineMotif.trim() || null); setShowDeclineModal(false); }}
                disabled={changingStatut}
                style={{ padding:'8px 16px', borderRadius:8, border:'none',
                  background:'#DC2626', color:'#fff', fontWeight:700,
                  cursor:changingStatut?'not-allowed':'pointer', opacity:changingStatut?.6:1 }}>
                {changingStatut ? t('common.saving') : t('phase_d4.decline_confirm_btn')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Documents (déplacé AVANT la gestion des paiements) ── */}
      <DocumentsDossier dossierId={dossierId} procedureId={procedureId} dossier={dossier}
        onStatusChange={async () => {
          try {
            const fresh = await authFetch(`${BASE}/sinistres/${procedureId}/dossiers/${dossierId}`).then(r => r.json());
            if (fresh && !fresh.error) {
              setDossier(fresh);
              setPaiementsSolde(Array.isArray(fresh.paiements_solde) ? fresh.paiements_solde : []);
              setSoldeLocked(!!fresh.paiements_solde_locked);
              setSoldeConfirme(!!fresh.solde_recu_confirme);
            }
          } catch(_) {}
        }}/>

      {/* ── Gestion du paiement du solde (après génération de l'appel de fonds) ── */}
      {!isVoyageur && (dossier.appel_fonds_confirme || dossier.statut === 'attente_paiement_solde' || dossier.statut === 'solde_paye') && (
        <div style={sectionCardStyle}>
          <div style={{ ...sectionTitleStyle, marginBottom:16 }}>
            <SectionIcon name="balance" />
            {t('phase_solde.section_title')}
          </div>

          {/* Ligne du solde dû + date limite */}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12,
            background:'#FFF7ED', border:'1px solid #FED7AA', borderRadius:8, padding:'12px 16px', marginBottom:16, flexWrap:'wrap' }}>
            <div>
              <div style={{ fontSize:11, fontWeight:700, color:'#9A3412', textTransform:'uppercase', letterSpacing:'.04em' }}>
                {t('phase_solde.balance_due')}
              </div>
              <div style={{ fontSize:22, fontWeight:800, color:'#EA580C', marginTop:2 }}>
                {soldeDu.toLocaleString('fr-FR',{minimumFractionDigits:2})} €
              </div>
            </div>
            {dossier.date_limite_paiement && (
              <div style={{ textAlign:'right' }}>
                <div style={{ fontSize:11, fontWeight:700, color:'#9A3412', textTransform:'uppercase', letterSpacing:'.04em' }}>
                  {t('phase_solde.deadline')}
                </div>
                <div style={{ fontSize:15, fontWeight:700, color:'#DC2626', marginTop:2 }}>
                  {new Date(dossier.date_limite_paiement).toLocaleDateString('fr-FR')}
                </div>
              </div>
            )}
          </div>

          {/* Liste des paiements reçus */}
          <div style={{ fontSize:12, fontWeight:700, color:'#64748B', textTransform:'uppercase',
            letterSpacing:'.04em', marginBottom:8 }}>
            {t('phase_solde.received_title')}
          </div>
          {paiementsSolde.length === 0 && (
            <div style={{ color:'#94A3B8', fontSize:13, textAlign:'center', padding:'8px 0 12px' }}>
              {t('phase_solde.received_empty')}
            </div>
          )}
          {paiementsSolde.map((p, i) => (
            <div key={p.id} style={{ display:'grid', gridTemplateColumns:'1fr 1fr 140px 36px', gap:10,
              alignItems:'end', marginBottom:10, paddingBottom:10,
              borderBottom: i<paiementsSolde.length-1 ? '1px solid #F1F5F9' : 'none' }}>
              <div>
                <label style={lbl}>{t('phase_solde.col_date')}</label>
                <input type="date" value={p.date_reception||''} disabled={isReadOnly||soldeLocked}
                  onChange={e=>updatePaiementSolde(p.id,'date_reception',e.target.value)}
                  style={{...inp, opacity:(isReadOnly||soldeLocked)?0.7:1}}/>
              </div>
              <div>
                <label style={lbl}>{t('phase_solde.col_emetteur')}</label>
                <input value={p.emetteur||''} disabled={isReadOnly||soldeLocked}
                  onChange={e=>updatePaiementSolde(p.id,'emetteur',e.target.value)}
                  placeholder={t('phase_solde.col_emetteur_ph')}
                  style={{...inp, opacity:(isReadOnly||soldeLocked)?0.7:1}}/>
              </div>
              <div>
                <label style={lbl}>{t('phase_solde.col_montant')}</label>
                <div style={{ position:'relative' }}>
                  <input type="number" value={p.montant||''} min="0" step="0.01" disabled={isReadOnly||soldeLocked}
                    onChange={e=>updatePaiementSolde(p.id,'montant',e.target.value)} placeholder="0.00"
                    style={{...inp, paddingRight:22, fontWeight:700, background:'#FAFFF7', opacity:(isReadOnly||soldeLocked)?0.7:1}}/>
                  <span style={{ position:'absolute',right:8,top:'50%',transform:'translateY(-50%)',
                    fontSize:12,color:'#059669',fontWeight:700,pointerEvents:'none' }}>€</span>
                </div>
              </div>
              {/* La suppression d'une ligne reste possible même verrouillé */}
              {!isReadOnly && !soldeConfirme && (
                <button onClick={()=>deletePaiementSolde(p.id)} title={t('common.delete')}
                  style={{ height:38, background:'#FFF5F5', border:'1px solid #FCA5A5', borderRadius:7,
                    cursor:'pointer', color:'#DC2626', fontSize:14 }}>🗑</button>
              )}
            </div>
          ))}

          {!isReadOnly && !soldeLocked && (
            <button onClick={addPaiementSolde} disabled={savingSolde}
              style={{ fontSize:13, color:'#2563EB', background:'none', border:'1px dashed #93C5FD',
                borderRadius:7, padding:'9px 16px', cursor:'pointer', width:'100%', fontWeight:600, marginBottom:16 }}>
              + {t('phase_solde.add_received')}
            </button>
          )}

          {/* Bouton Enregistrer / Modifier (verrouille les champs à l'enregistrement) */}
          {!isReadOnly && (
            <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:16 }}>
              {!soldeLocked ? (
                <button onClick={() => setShowSoldeSaveConfirm(true)} disabled={savingSolde || paiementsSolde.length===0}
                  style={{ background:'#F18A00', color:'#fff', border:'none', borderRadius:8,
                    padding:'9px 18px', fontSize:13, fontWeight:700,
                    cursor:(savingSolde||paiementsSolde.length===0)?'not-allowed':'pointer',
                    opacity:(savingSolde||paiementsSolde.length===0)?0.5:1 }}>
                  {savingSolde ? '…' : t('common.save')}
                </button>
              ) : (!soldeConfirme && (
                <button onClick={unlockSolde} disabled={savingSolde}
                  style={lockButtonStyle(true, savingSolde)}>
                  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"
                    dangerouslySetInnerHTML={{ __html: sectionIconPaths.unlock }} />
                  {t('phase_solde.unlock_btn')}
                </button>
              ))}
            </div>
          )}

          {/* Pied : total reçu + restant à recevoir */}
          <div style={{ display:'flex', gap:12, flexWrap:'wrap', borderTop:'0.5px solid #E6E9ED', paddingTop:14 }}>
            <div style={{ flex:1, minWidth:160, background:'#F0FDF4', border:'1px solid #A7F3D0',
              borderRadius:8, padding:'10px 14px' }}>
              <div style={{ fontSize:11, fontWeight:700, color:'#047857', textTransform:'uppercase', letterSpacing:'.04em' }}>
                {t('phase_solde.total_received')}
              </div>
              <div style={{ fontSize:18, fontWeight:800, color:'#059669', marginTop:2 }}>
                {totalRecuSolde.toLocaleString('fr-FR',{minimumFractionDigits:2})} €
              </div>
            </div>
            <div style={{ flex:1, minWidth:160, background: resteARecevoir>0 ? '#FEF2F2' : '#F0FDF4',
              border:`1px solid ${resteARecevoir>0 ? '#FECACA' : '#A7F3D0'}`, borderRadius:8, padding:'10px 14px' }}>
              <div style={{ fontSize:11, fontWeight:700, color: resteARecevoir>0 ? '#991B1B' : '#047857',
                textTransform:'uppercase', letterSpacing:'.04em' }}>
                {t('phase_solde.remaining')}
              </div>
              <div style={{ fontSize:18, fontWeight:800, color: resteARecevoir>0 ? '#DC2626' : '#059669', marginTop:2 }}>
                {resteARecevoir.toLocaleString('fr-FR',{minimumFractionDigits:2})} €
              </div>
            </div>
          </div>

          {/* Confirmer réception du solde : apparaît quand le restant atteint 0 */}
          {!isReadOnly && resteARecevoir === 0 && totalRecuSolde > 0 && (
            <div style={{ marginTop:16, display:'flex', justifyContent:'flex-end' }}>
              {!soldeConfirme ? (
                <button onClick={() => setShowSoldeRecuConfirm(true)} disabled={savingSolde}
                  style={{ background:'#059669', color:'#fff', border:'none', borderRadius:8,
                    padding:'11px 22px', fontSize:14, fontWeight:700,
                    cursor: savingSolde ? 'not-allowed' : 'pointer', opacity: savingSolde ? .6 : 1 }}>
                  {t('phase_solde.confirm_recu_btn')}
                </button>
              ) : (
                <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                  <span style={{ fontSize:14, fontWeight:700, color:'#047857', background:'#D1FAE5',
                    padding:'10px 18px', borderRadius:8 }}>
                    ✓ {t('phase_solde.recu_confirmed')}
                  </span>
                  {canValidate && (
                    <button onClick={annulerSolde} disabled={savingSolde}
                      style={{ fontSize:12, padding:'8px 14px', borderRadius:7, fontWeight:600,
                        background:'#fff', color:'#DC2626', border:'1px solid #FCA5A5', cursor:'pointer' }}>
                      {t('phase_solde.recu_cancel')}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Phase C : Section Prestataires (maintien voyage) ──── */}
      {!isVoyageur && isMaintien && (
      <div style={sectionCardStyle}>
        <div style={sectionHeaderRow}>
          <div>
            <SectionTitle icon="providers">{t('phase_c.section_title')}</SectionTitle>
          </div>
        </div>
        <div style={sectionIntroStyle}>{t('phase_c.section_subtitle')}</div>

        {prestairesDossier.length === 0 && (
          <div style={{ color:'#94A3B8', fontSize:13, textAlign:'center', padding:'18px 0' }}>
            {t('phase_c.empty')}
          </div>
        )}

        {prestairesDossier.map((p, idx) => {
          const ref = allPrestataires.find(ap => ap.id === p.prestataire_id);
          const iban = ref?.iban || p.iban || '';
          const swift = ref?.swift || p.swift || '';
          const ville = ref?.ville || p.ville || '';
          const pays  = ref?.pays  || p.pays  || '';
          return (
            <div key={p.id} style={{ border:'0.5px solid #E6E9ED', borderRadius:8, padding:14, marginBottom:10 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                <span style={{ fontSize:12, fontWeight:700, color:'#047857', fontFamily:'monospace' }}>
                  {t('phase_c.prestataire_num')}{idx + 1}
                </span>
                <button onClick={()=>removePrestataireRow(idx)} disabled={paiementsLocked||isReadOnly}
                  style={{ fontSize:11, padding:'3px 8px', borderRadius:5,
                    background:'#FEE2E2', color:'#991B1B', border:'none',
                    cursor:(paiementsLocked||isReadOnly)?'not-allowed':'pointer',
                    opacity:(paiementsLocked||isReadOnly)?0.5:1 }}>
                  {t('common.delete')}
                </button>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div style={{ gridColumn:'span 2' }}>
                  <label style={{ display:'block', fontSize:11, fontWeight:600, color:'#475569', marginBottom:4 }}>
                    {t('phase_c.select_prestataire')} *
                  </label>
                  <select value={p.prestataire_id || ''}
                    onChange={e => updatePrestataireRow(idx, 'prestataire_id', e.target.value)}
                    disabled={paiementsLocked||isReadOnly}
                    style={{ width:'100%', padding:'8px 10px', borderRadius:6, border:'1px solid #D9D9D5', fontSize:13, fontFamily:'inherit', cursor:(paiementsLocked||isReadOnly)?'not-allowed':'pointer', background:(paiementsLocked||isReadOnly)?'#F1F5F9':'#fff', opacity:(paiementsLocked||isReadOnly)?0.7:1 }}>
                    <option value="">— {t('phase_c.select_placeholder')} —</option>
                    {allPrestataires.map(ap => (
                      <option key={ap.id} value={ap.id}>
                        {ap.nom}{ap.ville ? ` · ${ap.ville}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ display:'block', fontSize:11, fontWeight:600, color:'#475569', marginBottom:4 }}>
                    {t('phase_c.amount')} (€) *
                  </label>
                  <input type="number" step="0.01" value={p.montant || ''}
                    onChange={e => updatePrestataireRow(idx, 'montant', e.target.value)}
                    disabled={paiementsLocked||isReadOnly}
                    style={{ width:'100%', padding:'8px 10px', borderRadius:6, border:'1px solid #D9D9D5', fontSize:13, fontFamily:'inherit', boxSizing:'border-box', background:(paiementsLocked||isReadOnly)?'#F1F5F9':'#fff', opacity:(paiementsLocked||isReadOnly)?0.7:1 }} />
                </div>
                <div>
                  <label style={{ display:'block', fontSize:11, fontWeight:600, color:'#475569', marginBottom:4 }}>
                    {t('phase_c.reference')}
                  </label>
                  <input value={p.reference || ''}
                    onChange={e => updatePrestataireRow(idx, 'reference', e.target.value)}
                    disabled={paiementsLocked||isReadOnly}
                    placeholder={t('phase_c.reference_placeholder')}
                    style={{ width:'100%', padding:'8px 10px', borderRadius:6, border:'1px solid #D9D9D5', fontSize:13, fontFamily:'inherit', boxSizing:'border-box', background:(paiementsLocked||isReadOnly)?'#F1F5F9':'#fff', opacity:(paiementsLocked||isReadOnly)?0.7:1 }} />
                </div>
                <div style={{ gridColumn:'span 2' }}>
                  <label style={{ display:'block', fontSize:11, fontWeight:600, color:'#475569', marginBottom:4 }}>
                    {t('phase_c.description')}
                  </label>
                  <input value={p.description || ''}
                    onChange={e => updatePrestataireRow(idx, 'description', e.target.value)}
                    disabled={paiementsLocked||isReadOnly}
                    placeholder={t('phase_c.description_placeholder')}
                    style={{ width:'100%', padding:'8px 10px', borderRadius:6, border:'1px solid #D9D9D5', fontSize:13, fontFamily:'inherit', boxSizing:'border-box', background:(paiementsLocked||isReadOnly)?'#F1F5F9':'#fff', opacity:(paiementsLocked||isReadOnly)?0.7:1 }} />
                </div>
              </div>

              {/* Affichage bancaire verrouillé (lecture seule depuis la fiche centrale) */}
              {p.prestataire_id && (iban || swift) && (
                <div style={{ marginTop:12, padding:10, background:'#F8FAFC', borderRadius:6, border:'1px dashed #CBD5E1' }}>
                  <div style={{ fontSize:11, fontWeight:600, color:'#64748B', marginBottom:6, display:'flex', alignItems:'center', gap:6 }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 11V8a7 7 0 0 1 14 0v3M5 11h14v9H5z"/>
                    </svg>
                    {t('phase_c.bank_locked')}
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, fontSize:12 }}>
                    <div><span style={{ color:'#94A3B8' }}>IBAN: </span><span style={{ fontFamily:'monospace' }}>{iban || '—'}</span></div>
                    <div><span style={{ color:'#94A3B8' }}>SWIFT: </span><span style={{ fontFamily:'monospace' }}>{swift || '—'}</span></div>
                    <div><span style={{ color:'#94A3B8' }}>{t('phase_c.location')}: </span>{[ville, pays].filter(Boolean).join(', ') || '—'}</div>
                  </div>
                </div>
              )}

              {/* Facture prestataire (liée à CETTE ligne de paiement, pas au prestataire) */}
              {p.prestataire_id && (() => {
                const rowSaved = p.id && !String(p.id).startsWith('new-');
                const fac = rowSaved ? factureFor(p.id) : null;
                return (
                  <div style={{ marginTop:10, padding:10, borderRadius:6,
                    background: fac ? '#F0FDF4' : '#FEF2F2', border:`1px solid ${fac ? '#A7F3D0' : '#FECACA'}` }}>
                    <div style={{ fontSize:11, fontWeight:700, color: fac ? '#047857' : '#991B1B', marginBottom:6 }}>
                      {fac ? '✓ ' : '⚠ '}{t('phase_d4_facture.title')}
                    </div>
                    {!rowSaved ? (
                      <div style={{ fontSize:12, color:'#92400E' }}>
                        {t('phase_d4_facture.save_row_first')}
                      </div>
                    ) : fac ? (
                      <div style={{ display:'flex', alignItems:'center', gap:10, fontSize:12, flexWrap:'wrap' }}>
                        <span style={{ fontWeight:600, color:'#334155' }}>📎 {fac.fichier_nom || fac.nom}</span>
                        <button onClick={() => ouvrirFacture(fac)}
                          style={{ fontSize:12, padding:'5px 10px', borderRadius:6, border:'1px solid #93C5FD',
                            background:'#EFF6FF', color:'#1D4ED8', cursor:'pointer', fontWeight:600 }}>
                          🔍 {t('phase_d4_facture.view')}
                        </button>
                        {!paiementsLocked && !isReadOnly && (
                          <label style={{ fontSize:11, color:'#64748B', cursor:'pointer', textDecoration:'underline' }}>
                            {t('phase_d4_facture.replace')}
                            <input type="file" style={{ display:'none' }}
                              onChange={e => uploadFacture(p.id, e.target.files[0], `Facture ${p.prestataire_nom||''}`)} />
                          </label>
                        )}
                      </div>
                    ) : (
                      !isReadOnly && !paiementsLocked && (
                        <label style={{ display:'inline-block', fontSize:12, padding:'6px 12px', borderRadius:6,
                          background:'#fff', border:'1px solid #F59E0B', color:'#92400E', cursor:'pointer', fontWeight:600 }}>
                          {t('phase_d4_facture.upload')}
                          <input type="file" style={{ display:'none' }}
                            onChange={e => uploadFacture(p.id, e.target.files[0], `Facture ${p.prestataire_nom||''}`)} />
                        </label>
                      )
                    )}
                  </div>
                );
              })()}
            </div>
          );
        })}

        {/* Total */}
        {prestairesDossier.length > 0 && (
          <div style={{ marginTop:10, padding:'10px 14px', background:theme.surfaceAlt, borderRadius:6,
            display:'flex', justifyContent:'space-between', alignItems:'center', border:`1px solid ${theme.border}` }}>
            <span style={{ fontSize:12, fontWeight:600, color:theme.textSecondary }}>{t('phase_c.total')}</span>
            <span style={{ fontSize:14, fontWeight:700, color:theme.text }}>
              {prestairesDossier.reduce((s, p) => s + (Number(p.montant) || 0), 0).toLocaleString('fr-FR', { minimumFractionDigits:2, maximumFractionDigits:2 })} €
            </span>
          </div>
        )}

        {/* Pied de section : ajouter / enregistrer / verrouiller — bas à droite */}
        {!isReadOnly && (
        <div style={sectionFooterStyle}>
          {!paiementsLocked && (
          <button onClick={addPrestataireRow}
            style={{ ...secondaryButtonStyle, padding:'8px 14px' }}>
            + {t('phase_c.add_prestataire')}
          </button>
          )}
          {canLock && prestairesDossier.length > 0 && (
            <button onClick={() => toggleLock('paiements', !paiementsLocked)} disabled={lockBusy}
              style={lockButtonStyle(paiementsLocked, lockBusy)}>
              <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"
                dangerouslySetInnerHTML={{ __html: paiementsLocked ? sectionIconPaths.unlock : sectionIconPaths.lock }} />
              {paiementsLocked ? t('phase_d4.unlock_paiements') : t('phase_d4.lock_paiements')}
            </button>
          )}
          {!paiementsLocked && (
          <button onClick={savePrestataires} disabled={savingPrestataires}
            style={{ fontSize:13, padding:'8px 18px', borderRadius:theme.radius,
              background:theme.orange, color:'#fff', border:'none',
              cursor: savingPrestataires ? 'not-allowed' : 'pointer', fontWeight:500,
              opacity: savingPrestataires ? .6 : 1 }}>
            {savingPrestataires ? t('common.saving') : t('common.save')}
          </button>
          )}
        </div>
        )}
      </div>
      )}

      {/* ── Gestion des paiements — masquée pour voyageur ──── */}
      {!isVoyageur && !isMaintien && (
      <div style={sectionCardStyle}>
        <div style={sectionHeaderRow}>
          <SectionTitle icon="money" locked={paiementsLocked} lockedLabel={t('phase_d4.locked')}>
            {t('sinistres.paiement_title')}
          </SectionTitle>
          <span style={{ fontSize:12, color:theme.textTertiary }}>{paiements.length} paiement{paiements.length>1?'s':''}</span>
        </div>

        {paiements.length === 0 && (
          <div style={{ color:'#94A3B8', fontSize:13, textAlign:'center', padding:'12px 0 16px' }}>
            {t('sinistres.paiement_aucun')}
          </div>
        )}

        {paiements.map((p, idx) => (
          <div key={p.id} style={{ border:'0.5px solid #E6E9ED', borderRadius:8,
            marginBottom:10, overflow:'hidden' }}>

            {/* En-tête du paiement */}
            <div style={{ background:'#F8FAFC', padding:'10px 14px',
              display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <span style={{ fontSize:12, fontWeight:800, color:'#F18A00', fontFamily:'monospace' }}>
                  {t('sinistres.paiement_numero')}{idx+1}
                </span>
                {!p.editing && p.nom && (
                  <span style={{ fontSize:13, fontWeight:600 }}>{[p.nom, p.prenom].filter(Boolean).join(' ')}</span>
                )}
                {!p.editing && p.montant && (
                  <span style={{ fontSize:13, fontWeight:700, color:'#059669' }}>
                    {parseFloat(p.montant).toLocaleString('fr-FR', {minimumFractionDigits:2})} €
                  </span>
                )}
              </div>
              <div style={{ display:'flex', gap:6 }}>
                {!p.editing && !isReadOnly && (
                  <button onClick={() => updatePaiement(p.id, 'editing', true)}
                    style={{ fontSize:12, padding:'3px 10px', borderRadius:5,
                      border:'0.5px solid #E6E9ED', cursor:'pointer', background:'#fff', color:'#475569' }}>
                    ✎
                  </button>
                )}
                {!isReadOnly && (
                <button onClick={() => { if(window.confirm('Supprimer ce paiement ?')) deletePaiement(p.id); }}
                  style={{ fontSize:12, padding:'3px 8px', borderRadius:5,
                    border:'1px solid #FCA5A5', cursor:'pointer', background:'#FFF5F5', color:'#DC2626' }}>
                  🗑
                </button>
                )}
              </div>
            </div>

            {/* Formulaire édition */}
            {p.editing && (
              <div style={{ padding:14 }}>
                {/* Type */}
                <div style={{ marginBottom:10 }}>
                  <label style={lbl}>{t('sinistres.paiement_type')}</label>
                  <div style={{ display:'flex', gap:8, marginTop:4 }}>
                    {[['particulier',t('sinistres.paiement_particulier')],['professionnel',t('sinistres.paiement_professionnel')]].map(([val,lbl2])=>(
                      <button key={val} type="button" onClick={()=>updatePaiement(p.id,'type',val)}
                        style={{ flex:1, padding:'7px', borderRadius:7, cursor:'pointer', fontSize:13,
                          fontWeight:p.type===val?700:400,
                          background:p.type===val?'#1A1A1A':'#F8FAFC',
                          color:p.type===val?'#fff':'#64748B',
                          border:`1.5px solid ${p.type===val?'#1A1A1A':'#E2E8F0'}` }}>
                        {lbl2}
                      </button>
                    ))}
                  </div>
                </div>
                {/* Si professionnel : nom de l'entreprise */}
                {p.type === 'professionnel' && (
                  <div style={{ marginBottom:10 }}>
                    <label style={lbl}>{t('phase_d4b.entreprise_nom')}</label>
                    <input value={p.entreprise_nom||''} onChange={e=>updatePaiement(p.id,'entreprise_nom',e.target.value)} style={inp}/>
                  </div>
                )}
                {/* Nom / Prénom (responsable légal si pro) */}
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:10 }}>
                  <div>
                    <label style={lbl}>{p.type==='professionnel' ? t('phase_d4b.resp_nom') : t('sinistres.paiement_nom')}</label>
                    <input value={p.nom||''} onChange={e=>updatePaiement(p.id,'nom',e.target.value)} style={inp}/>
                  </div>
                  <div>
                    <label style={lbl}>{p.type==='professionnel' ? t('phase_d4b.resp_prenom') : t('sinistres.paiement_prenom')}</label>
                    <input value={p.prenom||''} onChange={e=>updatePaiement(p.id,'prenom',e.target.value)} style={inp}/>
                  </div>
                </div>
                {/* Date de naissance + Nationalité (les deux cas) */}
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:10 }}>
                  <div>
                    <label style={lbl}>{t('phase_d4b.date_naissance')}</label>
                    <input type="date" value={p.date_naissance||''} onChange={e=>updatePaiement(p.id,'date_naissance',e.target.value)} style={inp}/>
                  </div>
                  <div>
                    <label style={lbl}>{t('phase_d4b.nationalite')}</label>
                    <select value={p.nationalite||''} onChange={e=>updatePaiement(p.id,'nationalite',e.target.value)} style={inp}>
                      <option value="">—</option>
                      {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ marginBottom:10 }}>
                  <label style={lbl}>{t('sinistres.paiement_adresse')}</label>
                  <input value={p.adresse||''} onChange={e=>updatePaiement(p.id,'adresse',e.target.value)} style={inp}/>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'120px 1fr', gap:10, marginBottom:10 }}>
                  <div>
                    <label style={lbl}>{t('sinistres.paiement_cp')}</label>
                    <input value={p.cp||''} onChange={e=>updatePaiement(p.id,'cp',e.target.value)} style={inp}/>
                  </div>
                  <div>
                    <label style={lbl}>{t('sinistres.paiement_ville')}</label>
                    <input value={p.ville||''} onChange={e=>updatePaiement(p.id,'ville',e.target.value)} style={inp}/>
                  </div>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:10 }}>
                  <div>
                    <label style={lbl}>{t('sinistres.paiement_iban')}</label>
                    <input value={p.iban||''} onChange={e=>updatePaiement(p.id,'iban',e.target.value.toUpperCase())}
                      placeholder="FR76 XXXX XXXX XXXX" style={inp}/>
                  </div>
                  <div>
                    <label style={lbl}>{t('sinistres.paiement_swift')}</label>
                    <input value={p.swift||''} onChange={e=>updatePaiement(p.id,'swift',e.target.value.toUpperCase())}
                      placeholder="Ex. BNPAFRPP" style={inp}/>
                  </div>
                </div>
                {/* Montant */}
                <div style={{ marginBottom:10 }}>
                  <label style={lbl}>{t('sinistres.paiement_montant')}</label>
                  <div style={{ position:'relative' }}>
                    <input type="number" value={p.montant||''} min="0" step="0.01"
                      onChange={e=>updatePaiement(p.id,'montant',e.target.value)}
                      placeholder="0.00"
                      style={{...inp, paddingRight:24, fontWeight:700, fontSize:15, background:'#FAFFF7'}}/>
                    <span style={{ position:'absolute',right:8,top:'50%',transform:'translateY(-50%)',
                      fontSize:13,color:'#059669',fontWeight:700,pointerEvents:'none' }}>€</span>
                  </div>
                </div>
                {/* Référence auto */}
                <div style={{ marginBottom:14 }}>
                  <label style={lbl}>
                    {t('sinistres.paiement_reference')}
                    <span style={{ fontSize:10,color:'#94A3B8',marginLeft:6,fontWeight:400 }}>
                      {t('sinistres.paiement_ref_auto')}
                    </span>
                  </label>
                  <div style={{ background:'#F1F5F9',borderRadius:7,padding:'9px 12px',
                    fontSize:13,fontFamily:'monospace',color:'#475569',letterSpacing:'.04em',fontWeight:700 }}>
                    {p.reference || genRef(idx)}
                  </div>
                </div>
                <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
                  <button onClick={() => closeEditPaiement(p.id)} disabled={savingPaiement}
                    style={{ fontSize:13,padding:'6px 16px',borderRadius:6,background:'#F18A00',
                      color:'#fff',border:'none',cursor:'pointer',fontWeight:700,opacity:savingPaiement?0.5:1 }}>
                    {savingPaiement ? '…' : t('common.save')}
                  </button>
                </div>
              </div>
            )}

            {/* Phase D4b : Recherche de sanction (par paiement, après enregistrement) */}
            {!p.editing && (
              <div style={{ padding:'0 14px 12px' }}>
                <div style={{ background: p.sanction_done ? '#F0FDF4' : '#FEF3C7',
                  border:`1px solid ${p.sanction_done ? '#A7F3D0' : '#FCD34D'}`, borderRadius:8, padding:'10px 12px',
                  display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                  <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:13,
                    color: p.sanction_done ? '#047857' : '#92400E', fontWeight:600,
                    cursor: (p.sanction_done || isReadOnly) ? 'default' : 'pointer' }}>
                    <input type="checkbox" checked={!!p.sanction_check_input || !!p.sanction_done}
                      disabled={p.sanction_done || isReadOnly}
                      onChange={e=>updatePaiement(p.id,'sanction_check_input',e.target.checked)} />
                    {t('phase_d4b.sanction_label')}
                  </label>
                  {!p.sanction_done && !isReadOnly && (
                    <button onClick={() => confirmSanction(p.id)}
                      disabled={!p.sanction_check_input || savingPaiement}
                      style={{ fontSize:12, padding:'5px 14px', borderRadius:6, fontWeight:700,
                        background: p.sanction_check_input ? '#059669' : '#CBD5E1', color:'#fff', border:'none',
                        cursor: p.sanction_check_input ? 'pointer' : 'not-allowed' }}>
                      {t('common.ok')}
                    </button>
                  )}
                  {p.sanction_done && <span style={{ fontSize:11, color:'#047857' }}>✓ {t('phase_d4b.sanction_confirmed')}</span>}
                </div>
              </div>
            )}

            {/* Résumé lecture */}
            {!p.editing && (
              <div style={{ padding:'10px 14px',
                display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10 }}>
                {[
                  [t('sinistres.paiement_nom'), [p.nom,p.prenom].filter(Boolean).join(' ')||'—'],
                  [t('sinistres.paiement_iban'), p.iban||'—'],
                  [t('sinistres.paiement_montant'), p.montant ? parseFloat(p.montant).toLocaleString('fr-FR',{minimumFractionDigits:2})+' €' : '—'],
                ].map(([label,value])=>(
                  <div key={label}>
                    <div style={{ fontSize:10,color:'#94A3B8',fontWeight:700,marginBottom:2 }}>{label.toUpperCase().replace(' (€)','')}</div>
                    <div style={{ fontSize:13,fontWeight:600,fontFamily:label.includes('IBAN')?'monospace':'inherit',
                      color:value==='—'?'#94A3B8':'#1A1A1A' }}>{value}</div>
                  </div>
                ))}
                <div style={{ gridColumn:'1/-1' }}>
                  <div style={{ fontSize:10,color:'#94A3B8',fontWeight:700,marginBottom:2 }}>RÉFÉRENCE</div>
                  <div style={{ fontSize:12,fontFamily:'monospace',fontWeight:700,color:'#475569' }}>
                    {p.reference || genRef(idx)}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}

        {/* Ajouter un paiement */}
        {!isReadOnly && !paiementsLocked && (
        <button onClick={addPaiement}
          style={{ width:'100%', padding:'10px', background:'none',
            border:'1px dashed #93C5FD', borderRadius:8, cursor:'pointer',
            fontSize:13, fontWeight:600, color:'#2563EB' }}>
          {t('sinistres.paiement_ajouter')}
        </button>
        )}

        {/* Phase D4 : Verrouiller / déverrouiller les paiements */}
        {canLock && paiements.length > 0 && (
          <div style={sectionFooterStyle}>
            <button onClick={() => toggleLock('paiements', !paiementsLocked)} disabled={lockBusy}
              style={lockButtonStyle(paiementsLocked, lockBusy)}>
              <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"
                dangerouslySetInnerHTML={{ __html: paiementsLocked ? sectionIconPaths.unlock : sectionIconPaths.lock }} />
              {paiementsLocked ? t('phase_d4.unlock_paiements') : t('phase_d4.lock_paiements')}
            </button>
          </div>
        )}
      </div>
      )}

      {/* ── Phase D3 : Section "Statut du dossier" (voyageur, en dernier) ── */}
      {isVoyageur && (
        <div style={{ background:'#fff', borderRadius:10, border:'0.5px solid #E6E9ED', padding:20, marginTop:16, marginBottom:16 }}>
          <div style={{ fontSize:14, fontWeight:700, marginBottom:14 }}>{t('voyageur_intro.status_section')}</div>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <span style={{ display:'inline-flex', alignItems:'center', gap:8,
              background:st.bg, color:st.color, borderRadius:99, padding:'8px 18px', fontSize:14, fontWeight:600 }}>
              <span style={{ width:9,height:9,borderRadius:'50%',background:st.dot }}/>
              {t('sinistres.dos_statut_' + dossier.statut)}
            </span>
          </div>
          {/* Message contextuel selon le statut */}
          <div style={{ fontSize:13, color:'#64748B', marginTop:14, lineHeight:1.5 }}>
            {dossier.statut === 'en_attente_informations' && t('voyageur_intro.status_msg_attente')}
            {dossier.statut === 'en_cours_traitement' && t('voyageur_intro.status_msg_traitement')}
            {dossier.statut === 'attente_validation' && t('voyageur_intro.status_msg_validation')}
            {dossier.statut === 'attente_paiement_solde' && t('voyageur_intro.status_msg_paiement')}
            {dossier.statut === 'solde_paye' && t('voyageur_intro.status_msg_solde_paye')}
            {dossier.statut === 'paiement_realise' && t('voyageur_intro.status_msg_realise')}
            {dossier.statut === 'decline' && t('voyageur_intro.status_msg_decline')}
          </div>
        </div>
      )}

      {/* ── Phase B : Bandeau motif de refus si présent ────── */}
      {dossier.motif_refus_manager && dossier.statut === 'en_cours_traitement' && !isVoyageur && (
        <div style={{ background:'#FFF7ED', border:'2px solid #F97316', borderRadius:10,
          padding:14, marginTop:14, display:'flex', alignItems:'flex-start', gap:12 }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#C2410C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 9v4M12 17h.01M10.29 3.86l-8.47 14.14a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71 -3l-8.47 -14.14a2 2 0 0 0 -3.42 0z"/>
          </svg>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:13, fontWeight:700, color:'#9A3412', marginBottom:4 }}>
              {t('phase_b.refus_banner_title')}
            </div>
            <div style={{ fontSize:13, color:'#7C2D12', whiteSpace:'pre-wrap' }}>
              {dossier.motif_refus_manager}
            </div>
          </div>
        </div>
      )}

      {/* ── Phase D4 : Demander la validation (conditionnel) + Décliner ────── */}
      {canSendValidation && (['en_attente_informations','en_cours_traitement'].includes(dossier.statut) || (isMaintien && dossier.statut === 'solde_paye')) && (
        <div style={{ marginTop:14 }}>
          {/* Checklist des conditions */}
          <div style={{ background:'#F8FAFC', border:'0.5px solid #E6E9ED', borderRadius:10, padding:16, marginBottom:12 }}>
            <div style={{ fontSize:13, fontWeight:700, marginBottom:10, color:'#334155' }}>{t('phase_d4.validation_checklist')}</div>
            <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
              {isMaintien ? (
                <CheckRow ok={auMoinsUnPrestataireAvecFacture} label={t('phase_solde.cond_prestataire_facture')} />
              ) : (
                <>
                  <CheckRow ok={paiementsLocked} label={t('phase_d4.cond_paiements_locked')} />
                  <CheckRow ok={docsAllApproved} label={`${t('phase_d4.cond_docs_approved')}${docsCount ? ` (${docsCount})` : ''}`} />
                  {paiements.length > 0 && <CheckRow ok={sanctionOk} label={t('phase_d4.cond_sanction')} />}
                  {isRapatriement && <CheckRow ok={paiementsProOntFacture} label={t('phase_d4.cond_facture')} />}
                </>
              )}
            </div>
          </div>

          {canRequestValidation ? (
            <div style={{ background:'#EFF6FF', border:'2px solid #3B82F6', borderRadius:10,
              padding:18, display:'flex', alignItems:'center', gap:16 }}>
              <span style={{ fontSize:22 }}>📤</span>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:14, fontWeight:700, color:'#1E40AF' }}>
                  {isMaintien ? t('phase_solde.send_validation_title') : t('phase_d4.request_validation_title')}
                </div>
                <div style={{ fontSize:12, color:'#1E40AF' }}>
                  {isMaintien ? t('phase_solde.send_validation_desc') : t('phase_d4.request_validation_desc')}
                </div>
              </div>
              <button onClick={() => setShowSendValidationConfirm(true)} disabled={changingStatut}
                style={{ fontSize:13, padding:'10px 18px', borderRadius:7,
                  background:'#2563EB', color:'#fff', border:'none', cursor:'pointer', fontWeight:700 }}>
                {isMaintien ? t('phase_solde.send_validation_btn') : t('phase_d4.request_validation_btn')}
              </button>
            </div>
          ) : (
            <div style={{ fontSize:12, color:'#94A3B8', fontStyle:'italic', padding:'0 4px' }}>
              {t('phase_d4.validation_locked_hint')}
            </div>
          )}

          {/* Décliner le dossier */}
          <div style={{ marginTop:12, display:'flex', justifyContent:'flex-end' }}>
            <button onClick={() => setShowDeclineModal(true)} disabled={changingStatut}
              style={{ fontSize:13, padding:'8px 16px', borderRadius:7,
                background:'#fff', color:'#DC2626', border:'1px solid #FCA5A5', cursor:'pointer', fontWeight:600 }}>
              ✕ {t('phase_d4.decline_btn')}
            </button>
          </div>
        </div>
      )}

      {/* ── Phase B : Validation Manager (statut attente_validation) ────── */}
      {dossier.statut === 'attente_validation' && canValidate && (
        <div style={{ background:'#F5F3FF', border:'2px solid #8B5CF6', borderRadius:10,
          padding:18, marginTop:14, display:'flex', alignItems:'center', gap:16 }}>
          <span style={{ fontSize:24 }}>🔐</span>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:14, fontWeight:700, color:'#6D28D9' }}>{t('phase_b.manager_validation_title')}</div>
            <div style={{ fontSize:12, color:'#6D28D9' }}>{t('phase_b.manager_validation_desc')}</div>
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={() => { setRefuseMotif(''); setShowRefuseModal(true); }}
              disabled={changingStatut}
              style={{ fontSize:13, padding:'7px 14px', borderRadius:7,
                background:'#fff', color:'#DC2626', border:'1px solid #FCA5A5', cursor:'pointer', fontWeight:600 }}>
              ✕ {t('phase_b.refuse_btn')}
            </button>
            <button onClick={validerDossier} disabled={changingStatut}
              style={{ fontSize:13, padding:'7px 14px', borderRadius:7,
                background:'#7C3AED', color:'#fff', border:'none', cursor:'pointer', fontWeight:700 }}>
              ✓ {t('phase_b.validate_btn')}
            </button>
          </div>
        </div>
      )}

      {/* ── Phase B : Modal de saisie du motif de refus ────────── */}
      {showRefuseModal && (
        <div onClick={() => !changingStatut && setShowRefuseModal(false)}
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.5)',
            display:'flex', alignItems:'center', justifyContent:'center', zIndex:1200 }}>
          <div onClick={e=>e.stopPropagation()}
            style={{ background:'#fff', borderRadius:12, padding:24, width:'min(480px, calc(100% - 32px))' }}>
            <div style={{ fontSize:17, fontWeight:800, color:'#161616', marginBottom:6 }}>{t('phase_b.refuse_modal_title')}</div>
            <div style={{ fontSize:12, color:'#64748B', marginBottom:16 }}>{t('phase_b.refuse_modal_desc')}</div>
            <textarea value={refuseMotif} onChange={e=>setRefuseMotif(e.target.value)}
              placeholder={t('phase_b.refuse_motif_placeholder')}
              style={{ width:'100%', minHeight:120, padding:'10px 12px', borderRadius:8,
                border:'1px solid #D9D9D5', fontSize:13, fontFamily:'inherit', resize:'vertical', boxSizing:'border-box' }} />
            <div style={{ marginTop:16, display:'flex', justifyContent:'flex-end', gap:10 }}>
              <button onClick={()=>setShowRefuseModal(false)} disabled={changingStatut}
                style={{ padding:'8px 16px', borderRadius:8, border:'1px solid #E5E7EB', background:'#fff', cursor:'pointer' }}>
                {t('common.cancel')}
              </button>
              <button onClick={async ()=>{
                  if (!refuseMotif.trim()) return;
                  await refuserDossier(refuseMotif.trim());
                  setShowRefuseModal(false);
                }}
                disabled={changingStatut || !refuseMotif.trim()}
                style={{ padding:'8px 16px', borderRadius:8, border:'none',
                  background:'#DC2626', color:'#fff', fontWeight:600,
                  cursor:(changingStatut||!refuseMotif.trim())?'not-allowed':'pointer',
                  opacity:(changingStatut||!refuseMotif.trim())?.6:1 }}>
                {changingStatut ? t('common.saving') : t('phase_b.refuse_confirm_btn')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Phase B : Modal de confirmation Marquer payé ────────── */}
      {showMarkPaidConfirm && (
        <div onClick={() => !changingStatut && setShowMarkPaidConfirm(false)}
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.5)',
            display:'flex', alignItems:'center', justifyContent:'center', zIndex:1200 }}>
          <div onClick={e=>e.stopPropagation()}
            style={{ background:'#fff', borderRadius:12, padding:24, width:'min(440px, calc(100% - 32px))' }}>
            <div style={{ fontSize:17, fontWeight:800, color:'#161616', marginBottom:6 }}>{t('phase_b.mark_paid_confirm_title')}</div>
            <div style={{ fontSize:13, color:'#475569', marginBottom:18 }}>{t('phase_b.mark_paid_confirm_desc')}</div>
            <div style={{ display:'flex', justifyContent:'flex-end', gap:10 }}>
              <button onClick={()=>setShowMarkPaidConfirm(false)} disabled={changingStatut}
                style={{ padding:'8px 16px', borderRadius:8, border:'1px solid #E5E7EB', background:'#fff', cursor:'pointer' }}>
                {t('common.cancel')}
              </button>
              <button onClick={async ()=>{ await marquerPaye(); setShowMarkPaidConfirm(false); }}
                disabled={changingStatut}
                style={{ padding:'8px 16px', borderRadius:8, border:'none',
                  background:'#059669', color:'#fff', fontWeight:700,
                  cursor:changingStatut?'not-allowed':'pointer', opacity:changingStatut?.6:1 }}>
                {changingStatut ? t('common.saving') : t('phase_b.mark_paid_confirm_btn')}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ── Modal : confirmation enregistrement du solde (verrouille) ── */}
      {showSoldeSaveConfirm && (
        <div onClick={() => !savingSolde && setShowSoldeSaveConfirm(false)}
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.5)',
            display:'flex', alignItems:'center', justifyContent:'center', zIndex:1200 }}>
          <div onClick={e=>e.stopPropagation()}
            style={{ background:'#fff', borderRadius:12, padding:24, width:'min(440px, calc(100% - 32px))' }}>
            <div style={{ fontSize:17, fontWeight:800, color:'#161616', marginBottom:6 }}>{t('phase_solde.save_confirm_title')}</div>
            <div style={{ fontSize:13, color:'#475569', marginBottom:18 }}>{t('phase_solde.save_confirm_desc')}</div>
            <div style={{ display:'flex', justifyContent:'flex-end', gap:10 }}>
              <button onClick={()=>setShowSoldeSaveConfirm(false)} disabled={savingSolde}
                style={{ padding:'8px 16px', borderRadius:8, border:'1px solid #E5E7EB', background:'#fff', cursor:'pointer' }}>
                {t('common.cancel')}
              </button>
              <button onClick={lockSolde} disabled={savingSolde}
                style={{ padding:'8px 16px', borderRadius:8, border:'none',
                  background:'#F18A00', color:'#fff', fontWeight:700,
                  cursor:savingSolde?'not-allowed':'pointer', opacity:savingSolde?.6:1 }}>
                {savingSolde ? t('common.saving') : t('phase_solde.save_confirm_btn')}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ── Modal : confirmation réception du solde ── */}
      {showSoldeRecuConfirm && (
        <div onClick={() => !savingSolde && setShowSoldeRecuConfirm(false)}
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.5)',
            display:'flex', alignItems:'center', justifyContent:'center', zIndex:1200 }}>
          <div onClick={e=>e.stopPropagation()}
            style={{ background:'#fff', borderRadius:12, padding:24, width:'min(440px, calc(100% - 32px))' }}>
            <div style={{ fontSize:17, fontWeight:800, color:'#161616', marginBottom:6 }}>{t('phase_solde.recu_confirm_title')}</div>
            <div style={{ fontSize:13, color:'#475569', marginBottom:18 }}>{t('phase_solde.recu_confirm_desc')}</div>
            <div style={{ display:'flex', justifyContent:'flex-end', gap:10 }}>
              <button onClick={()=>setShowSoldeRecuConfirm(false)} disabled={savingSolde}
                style={{ padding:'8px 16px', borderRadius:8, border:'1px solid #E5E7EB', background:'#fff', cursor:'pointer' }}>
                {t('common.cancel')}
              </button>
              <button onClick={confirmerSolde} disabled={savingSolde}
                style={{ padding:'8px 16px', borderRadius:8, border:'none',
                  background:'#059669', color:'#fff', fontWeight:700,
                  cursor:savingSolde?'not-allowed':'pointer', opacity:savingSolde?.6:1 }}>
                {savingSolde ? t('common.saving') : t('phase_solde.recu_confirm_btn')}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ── Modal : confirmation envoi pour validation manager ── */}
      {showSendValidationConfirm && (
        <div onClick={() => !changingStatut && setShowSendValidationConfirm(false)}
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.5)',
            display:'flex', alignItems:'center', justifyContent:'center', zIndex:1200 }}>
          <div onClick={e=>e.stopPropagation()}
            style={{ background:'#fff', borderRadius:12, padding:24, width:'min(460px, calc(100% - 32px))' }}>
            <div style={{ fontSize:17, fontWeight:800, color:'#161616', marginBottom:6 }}>{t('phase_solde.send_validation_confirm_title')}</div>
            <div style={{ fontSize:13, color:'#475569', marginBottom:18 }}>{t('phase_solde.send_validation_confirm_desc')}</div>
            <div style={{ display:'flex', justifyContent:'flex-end', gap:10 }}>
              <button onClick={()=>setShowSendValidationConfirm(false)} disabled={changingStatut}
                style={{ padding:'8px 16px', borderRadius:8, border:'1px solid #E5E7EB', background:'#fff', cursor:'pointer' }}>
                {t('common.cancel')}
              </button>
              <button onClick={async ()=>{ await envoyerValidation(); setShowSendValidationConfirm(false); }}
                disabled={changingStatut}
                style={{ padding:'8px 16px', borderRadius:8, border:'none',
                  background:'#2563EB', color:'#fff', fontWeight:700,
                  cursor:changingStatut?'not-allowed':'pointer', opacity:changingStatut?.6:1 }}>
                {changingStatut ? t('common.saving') : t('phase_solde.send_validation_confirm_btn')}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ── Modal : passer en cours de traitement (admin) ──────── */}
      {showStartProcessing && (
        <div onClick={()=>!startingProcessing && setShowStartProcessing(false)}
          style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',display:'flex',
            alignItems:'center',justifyContent:'center',zIndex:1200}}>
          <div onClick={e=>e.stopPropagation()}
            style={{ background:'#fff', borderRadius:12, padding:24, width:'min(460px, calc(100% - 32px))' }}>
            <div style={{ fontSize:17, fontWeight:800, color:'#161616', marginBottom:6 }}>{t('sinistres.passer_en_cours_title')}</div>
            <div style={{ fontSize:13, color:'#475569', marginBottom:18, lineHeight:1.5 }}>{t('sinistres.passer_en_cours_desc')}</div>
            <div style={{ display:'flex', justifyContent:'flex-end', gap:10 }}>
              <button onClick={()=>setShowStartProcessing(false)} disabled={startingProcessing}
                style={{ padding:'8px 16px', borderRadius:8, border:'1px solid #E5E7EB', background:'#fff', cursor:'pointer' }}>
                {t('common.cancel')}
              </button>
              <button onClick={startProcessing} disabled={startingProcessing}
                style={{ padding:'8px 18px', borderRadius:8, border:'none',
                  background:'#1A1A1A', color:'#fff', fontWeight:500,
                  cursor:startingProcessing?'not-allowed':'pointer', opacity:startingProcessing?.6:1 }}>
                {startingProcessing ? t('common.saving') : t('sinistres.passer_en_cours')}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ── Modal Créer accès voyageur ────────────────────── */}
      {showVoyageurModal && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.45)',display:'flex',
          alignItems:'center',justifyContent:'center',zIndex:1200}}>
          <div style={{background:'#fff',borderRadius:12,padding:24,width:460,maxWidth:'92vw'}}>
            <div style={{fontSize:15,fontWeight:800,marginBottom:4}}><SvgIcon size={15}><path d="M16.555 3.843l3.602 3.602a2.877 2.877 0 0 1 0 4.069l-2.643 2.643a2.877 2.877 0 0 1 -4.069 0l-.301 -.301l-6.558 6.558a2 2 0 0 1 -1.239 .578l-.175 .007h-1.977a1 1 0 0 1 -.993 -.883l-.007 -.117v-1.977a2 2 0 0 1 .467 -1.284l.119 -.13l.414 -.414h2v-2h2v-2l2.144 -2.144l-.301 -.301a2.877 2.877 0 0 1 0 -4.069l2.643 -2.643a2.877 2.877 0 0 1 4.069 0zM15 9h.01"/></SvgIcon> {t('sinistres.creer_acces_voyageur')}</div>
            <div style={{fontSize:12,color:'#64748B',marginBottom:16}}>{t('sinistres.creer_acces_subtitle')}</div>
            {voyageurCreated ? (
              <div style={{background:'#F0FDFA',border:'1px solid #14B8A6',borderRadius:8,padding:16,marginBottom:16}}>
                <div style={{fontSize:13,fontWeight:700,color:'#0F766E',marginBottom:8}}>
                  ✓ {t('sinistres.acces_cree')}
                </div>
                <div style={{fontSize:13,marginBottom:4}}>📧 <strong>Email :</strong> {voyageurCreated.email}</div>
                <div style={{fontSize:13}}>🔐 <strong>Mot de passe :</strong>
                  <code style={{background:'#E0FDF4',padding:'2px 8px',borderRadius:4,marginLeft:6,fontWeight:700}}>
                    {voyageurPwd}
                  </code>
                </div>
              </div>
            ) : (
              <>
                {voyageurError && (
                  <div style={{background:'#FEE2E2',borderRadius:7,padding:'8px 12px',
                    fontSize:13,color:'#991B1B',marginBottom:12}}>⚠ {voyageurError}</div>
                )}
                <div style={{marginBottom:12}}>
                  <label style={lbl}>{t('sinistres.acces_email')}</label>
                  <input type="email" value={voyageurEmail} onChange={e=>setVoyageurEmail(e.target.value)}
                    style={inp}/>
                </div>
                <div style={{marginBottom:16}}>
                  <label style={lbl}>{t('sinistres.acces_password')}</label>
                  <div style={{display:'flex',gap:8}}>
                    <input value={voyageurPwd} onChange={e=>setVoyageurPwd(e.target.value)}
                      style={{...inp,fontFamily:'monospace',fontWeight:700}}/>
                    <button onClick={()=>setVoyageurPwd(genPassword())}
                      style={{padding:'7px 10px',borderRadius:6,border:'0.5px solid #E6E9ED',
                        background:'#F8FAFC',cursor:'pointer',fontSize:12}}>
                      🔄
                    </button>
                  </div>
                </div>
              </>
            )}
            <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
              <button onClick={()=>setShowVoyageurModal(false)}
                style={{fontSize:14,padding:'7px 16px',borderRadius:6,
                  border:'1px solid #CBD5E1',cursor:'pointer',background:'#fff'}}>
                {voyageurCreated ? t('common.close') : t('common.cancel')}
              </button>
              {!voyageurCreated && (
                <button onClick={createVoyageurAcces}
                  disabled={!voyageurEmail.trim()||!voyageurPwd.trim()||voyageurSaving}
                  style={{fontSize:14,padding:'7px 16px',borderRadius:6,background:'#0F766E',
                    color:'#fff',border:'none',cursor:'pointer',fontWeight:700,
                    opacity:(!voyageurEmail.trim()||voyageurSaving)?0.5:1}}>
                  {voyageurSaving ? '…' : t('sinistres.acces_generer')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
